import { uniqueId } from 'lodash';
import { computed, makeAutoObservable } from 'mobx';
import { groupByFunction } from '../../../stores/websocketStore/utils/rxjs/groupByFunction';
import { returnValidOptions } from './returnValidOptions';

export class OptionManagement {
  websocketStore = null;
  tradePlanForm = null;
  requestKey = null;
  dataSource = 'LIVE';
  streamsInitialized = false;

  expirationDates = [];
  selectedExpirationDates = [];

  optionChains = {};
  optionDataReferenceMap = {};
  underlyingPrice = null;
  optionChainSelectionMap = {};

  optionChainStrategy = {};
  callsFilter = 'none';
  putsFilter = 'none';
  buyOrSellOption = 'LONG';
  OTM = false;
  ITM = false;
  notExpiringToday = false;
  optionFilters = {
    tightSpreadPct: false,
    tightSpreadCents: false,
    optionPriceRange: false,
    qtyRange: false,
    dollarRange: false,
  };
  optionFilterDetails = {
    tightSpreadPct: {
      maxOptionSpreadPct: 0.05,
    },
    tightSpreadCents: {
      maxOptionSpreadCents: 0,
    },
    qtyRange: {
      minQty: 0,
      maxQty: 0,
    },
    optionPriceRange: {
      minPrice: 0,
      maxPrice: 0,
    },
    dollarRange: {
      minDollar: 0,
      maxDollar: 0,
    },
  };
  constructor(websocketStore, tradePlanForm, initialData = {}) {
    makeAutoObservable(this, { returnValidOptions: computed });
    this.websocketStore = websocketStore;
    this.tradePlanForm = tradePlanForm;
    this.requestKey = uniqueId();
    this.initializeStreams();
    this.streamsInitialized = true;
    this.validOptionCount = 0;
    if (initialData) {
      Object.keys(initialData).forEach((field) => {
        if (field === 'optionFilters') {
          const filterArr = Object.keys(initialData.optionFilters);
          if (filterArr.length > 0) {
            filterArr.forEach((filter) => (this.optionFilters[filter] = true));
            this.optionFilterDetails = initialData.optionFilters;
          }
        } else if (field === 'expirationDates') {
          const selectedDates = Array.isArray(initialData.expirationDates)
            ? initialData.expirationDates.map((date) =>
                OptionManagement.makeDateObject(date)
              )
            : [];
          this.expirationDates = selectedDates;
          this.selectedExpirationDates = selectedDates;
        } else if (field === 'optionChoices' || field === 'expirationDates') {
          return null;
        } else {
          this[field] = initialData[field];
        }
      });
    }
  }
  updateData = (data) => {
    Object.keys(data).forEach((field) => {
      if (field === 'optionFilters') {
        const filterArr = Object.keys(data.optionFilters);
        if (filterArr.length > 0) {
          filterArr.forEach((filter) => (this.optionFilters[filter] = true));
          this.optionFilterDetails = data.optionFilters;
        }
      } else if (field === 'expirationDates') {
        const selectedDates = Array.isArray(data.expirationDates)
          ? data.expirationDates.map((date) =>
              OptionManagement.makeDateObject(date)
            )
          : [];
        this.expirationDates = selectedDates;
        this.selectedExpirationDates = selectedDates;
      } else if (field === 'optionChoices' || field === 'expirationDates') {
        return null;
      } else {
        this[field] = data[field];
      }
    });
  };

  initializeStreams = () => {
    const expirationDatesStream = groupByFunction(
      'messageType',
      'optionExpirationDates',
      this.websocketStore.websocketSubject
    );
    expirationDatesStream.subscribe((msg) =>
      this.handleExpirationDatesStream(msg)
    );

    const optionChainStream = groupByFunction(
      'messageType',
      'requestOptionChains',
      this.websocketStore.websocketSubject
    );
    const matchedIdStream = groupByFunction(
      'id',
      this.requestKey,
      optionChainStream
    );
    matchedIdStream.subscribe((msg) => this.handleOptionChainStream(msg));
  };

  // Parses Java Date.toString() format: "Thu Jan 14 00:00:00 EST 2027"
  // new Date(str) is unreliable for this format in all browsers
  static parseExpirationDate(dateStr) {
    const MONTH_INDEX = {Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11};
    const arr = dateStr.trim().split(/\s+/);
    if (arr.length < 6) return null;
    const month = MONTH_INDEX[arr[1]];
    if (month === undefined) return null;
    const day = parseInt(arr[2], 10);
    const year = parseInt(arr[5], 10);
    if (isNaN(day) || isNaN(year)) return null;
    return new Date(year, month, day);
  }

  handleExpirationDatesStream = (msg) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Filter out past dates, then sort chronologically
    const futureDates = msg.dates.filter((date) => {
      const d = OptionManagement.parseExpirationDate(date);
      return d !== null && d >= today;
    });
    futureDates.sort((a, b) => {
      const da = OptionManagement.parseExpirationDate(a);
      const db = OptionManagement.parseExpirationDate(b);
      if (!da || !db) return 0;
      return da - db;
    });
    this.expirationDates = futureDates.map((date) =>
      OptionManagement.makeDateObject(date)
    );
    // Default to the first two valid future dates (skip today if notExpiringToday)
    const defaultDates = this.notExpiringToday
      ? futureDates.filter((date) => {
          const d = OptionManagement.parseExpirationDate(date);
          return d !== null && d > today;
        })
      : futureDates;
    this.selectedExpirationDates = defaultDates
      .slice(0, 2)
      .map((date) => OptionManagement.makeDateObject(date));
  };

  static makeDateObject(date) {
    return {
      value: date,
      label: OptionManagement.parseDateLabel(date),
    };
  }

  static parseDateLabel(dateStr) {
    const d = OptionManagement.parseExpirationDate(dateStr);
    if (!d) {
      const arr = dateStr.split(' ');
      return arr.length >= 3 ? arr[1] + ' ' + arr[2] : dateStr;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dte = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
    const arr = dateStr.trim().split(/\s+/);
    if (arr.length < 6) return dateStr;
    const [, monthName, day, timeStr, tz, year] = arr;
    const isMidnight = timeStr === '00:00:00';
    if (isMidnight) {
      return `${monthName} ${day}, ${year} (${dte}d)`;
    }
    const [hh, mm] = timeStr.split(':');
    return `${monthName} ${day} ${hh}:${mm} ${tz} ${year} (${dte}d)`;
  }
  handleOptionChainStream = (msg) => {
    this.optionChainSelectionMap = {};
    this.optionChains = {};
    this.optionDataReferenceMap = {};
    const { bid, ask } = msg.quote;
    this.underlyingPrice = (bid + ask) / 2;
    const chains = msg.chains;
    chains.forEach((chain, index) => {
      const [date, chainArr] = Object.entries(chain)[0];
      if (!this.optionChainSelectionMap[date]) {
        this.optionChainSelectionMap[date] = {};
      }
      chainArr.forEach((item) => {
        this.optionDataReferenceMap[item.symbol] = item;
        let value = false;
        if (item.option_type === 'put') {
          if (this.putsFilter === 'front' && index === 0) value = true;
          if (this.putsFilter === 'all') value = true;
        }
        if (item.option_type === 'call') {
          if (this.callsFilter === 'front' && index === 0) value = true;
          if (this.callsFilter === 'all') value = true;
        }
        if (!this.optionChainSelectionMap[date][item.symbol]) {
          this.optionChainSelectionMap[date][item.symbol] = value;
        }
      });
      // add intermediate date key
      this.optionChains[date] = chainArr.reduce(
        (acc, item) => {
          if (item.option_type === 'call') {
            acc.calls.push(item);
            return acc;
          } else {
            acc.puts.push(item);
            return acc;
          }
        },
        { calls: [], puts: [] }
      );
    });
  };

  setCallsFilter = (string) => {
    this.callsFilter = string;
    switch (string) {
      case 'none':
        Object.keys(this.optionChains).forEach((date) => {
          const calls = this.optionChains[date].calls;
          calls.forEach(({ symbol }) => {
            this.optionChainSelectionMap[date][symbol] = false;
          });
        });
        break;
      case 'front':
        Object.keys(this.optionChains).forEach((date, index) => {
          const calls = this.optionChains[date].calls;
          calls.forEach(({ symbol }) => {
            if (index === 0) this.optionChainSelectionMap[date][symbol] = true;
            else this.optionChainSelectionMap[date][symbol] = false;
          });
        });
        break;
      case 'all':
        Object.keys(this.optionChains).forEach((date) => {
          const calls = this.optionChains[date].calls;
          calls.forEach(({ symbol }) => {
            this.optionChainSelectionMap[date][symbol] = true;
          });
        });
        break;
      default:
        break;
    }
  };
  setPutsFilter = (string) => {
    this.putsFilter = string;
    switch (string) {
      case 'none':
        Object.keys(this.optionChains).forEach((date) => {
          const puts = this.optionChains[date].puts;
          puts.forEach(({ symbol }) => {
            this.optionChainSelectionMap[date][symbol] = false;
          });
        });
        break;
      case 'front':
        Object.keys(this.optionChains).forEach((date, index) => {
          const puts = this.optionChains[date].puts;
          puts.forEach(({ symbol }) => {
            if (index === 0) this.optionChainSelectionMap[date][symbol] = true;
            else this.optionChainSelectionMap[date][symbol] = false;
          });
        });
        break;
      case 'all':
        Object.keys(this.optionChains).forEach((date) => {
          const puts = this.optionChains[date].puts;
          puts.forEach(({ symbol }) => {
            this.optionChainSelectionMap[date][symbol] = true;
          });
        });
        break;
      default:
        break;
    }
  };

  get selectedOptions() {
    let callsArr = [];
    let putsArr = [];

    const datesArr = Object.keys(this.optionChains);
    datesArr.forEach((date) => {
      const calls = this.optionChains[date].calls.filter(
        (call) => this.optionChainSelectionMap[date][call.symbol]
      );
      const puts = this.optionChains[date].puts.filter(
        (put) => this.optionChainSelectionMap[date][put.symbol]
      );
      callsArr = [...callsArr, ...calls];
      putsArr = [...putsArr, ...puts];
    });
    return {
      calls: callsArr,
      puts: putsArr,
    };
  }
  setDataSource = (string) => {
    this.dataSource = string;
  };
  setBuyOrSellOption = (string) => {
    this.buyOrSellOption = string;
  };
  setOtm = (bool) => {
    this.OTM = bool;
  };
  setItm = (bool) => {
    this.ITM = bool;
  };
  setNotExpiringToday = (bool) => {
    this.notExpiringToday = bool;
  };
  setSelectedExpirationDates = (array) => {
    if (array.length === 0) {
      this.optionChains = {};
      this.optionChainSelectionMap = {};
    }
    this.selectedExpirationDates = array;
  };
  addSelectedExpirationDate = (date) => {
    this.selectedExpirationDates.push(date);
  };
  removeSelectedExpirationDate = (index) => {
    const filteredArr = this.selectedExpirationDates.filter(
      (_, i) => i !== index
    );
    this.selectedExpirationDates = filteredArr;
  };
  clearSelectedExpirationDates = () => {
    this.selectedExpirationDates = [];
  };
  setOptionChainSelectionMapValue = (symbol, date, bool) => {
    if (!symbol || !date) return;
    if (this.optionChainSelectionMap[date]) {
      this.optionChainSelectionMap[date][symbol] = bool;
    }
  };
  setAllSymbolsOfDate = (date, bool, type) => {
    const chains = this.optionChains[date][type];
    chains.forEach(
      (chain) => (this.optionChainSelectionMap[date][chain.symbol] = bool)
    );
  };
  deselectAllOptionChains = () => {
    for (const date in this.optionChainSelectionMap) {
      for (const symbol in this.optionChainSelectionMap[date]) {
        this.optionChainSelectionMap[date][symbol] = false;
      }
    }
  };
  setOptionChainStrategy = (string) => {
    switch (string) {
      case 'OTM1':
        this.optionChainStrategy = {
          OTM1: {},
        };
        break;
      case 'ITM1':
        this.optionChainStrategy = {
          ITM1: {},
        };
        break;
      case 'TIGHTEST_SPREAD':
        this.optionChainStrategy = {
          hugCurrentPrice: {
            numOfStrikes: 5,
          },
        };
        break;
      default:
        return null;
    }
  };
  get optionChainStrategyString() {
    if (this.optionChainStrategy.OTM1) return 'OTM1';
    else if (this.optionChainStrategy.ITM1) return 'ITM1';
    else return 'TIGHTEST_SPREAD';
  }

  setNumberOfStrikes = (number) => {
    if (this.optionChainStrategy.hugCurrentPrice)
      this.optionChainStrategy.hugCurrentPrice.numOfStrikes = number;
  };
  setOptionFilterBool = (filter, bool) => {
    this.optionFilters[filter] = bool;
  };
  setMinPrice = (number) => {
    this.optionFilterDetails.optionPriceRange.minPrice = number;
  };
  setMaxPrice = (number) => {
    this.optionFilterDetails.optionPriceRange.maxPrice = number;
  };
  setMinQty = (number) => {
    this.optionFilterDetails.qtyRange.minQty = number;
  };
  setMaxQty = (number) => {
    this.optionFilterDetails.qtyRange.maxQty = number;
  };
  setMaxOptionSpreadCents = (number) => {
    this.optionFilterDetails.tightSpreadCents.maxOptionSpreadCents = number;
  };
  setMaxOptionSpreadPct = (number) => {
    this.optionFilterDetails.tightSpreadPct.maxOptionSpreadPct = number;
  };

  constructOptionFilterObject = () => {
    return Object.entries(this.optionFilters).reduce((acc, item) => {
      if (item[1] === true) {
        acc[item[0]] = this.optionFilterDetails[item[0]];
      }
      return acc;
    }, {});
  };
  formatOptionChoices = () => {
    const resultObj = {};
    const keyValuePair = Object.entries(this.optionChainSelectionMap);
    keyValuePair.forEach(([date, symbolObj]) => {
      resultObj[date] = Object.keys(symbolObj).filter(
        (symbol) => this.optionChainSelectionMap[date][symbol]
      );
    });
    return resultObj;
  };

  get validOptions() {
    const vopt = returnValidOptions(this);
    this.validOptionCount = Object.keys(vopt).length;
    return vopt;
  }
  /*
    current format: [{value: date, label: shortenedDate}, {value: date, label: shortenedDate}, {value: date, label: shortenedDate}]
    desired format: [date, date, date]
  
  
  */
  returnJson = () => {
    return {
      optionChoices: {
        choices: this.formatOptionChoices(),
        dataSource: this.dataSource,
      },
      expirationDates: this.selectedExpirationDates.map((obj) => obj.value),
      buyOrSellOption: this.buyOrSellOption,
      OTM: this.OTM,
      ITM: this.ITM,
      optionChainStrategy: this.optionChainStrategy,
      optionFilters: this.constructOptionFilterObject(),
    };
  };
}
// tp formatting
//"optionManagement": {
//       "optionChoices": {
//           "choices": {
//               "2022-03-25": [
//                   "AAPL220325C00117000",
//                   "AAPL220325P00119000"
//               ],
//               "2022-03-18": [
//                   "AAPL220318C00070000",
//                   "AAPL220318C00105000",
//                   "AAPL220318C00185000",
//                   "AAPL220318P00085000",
//                   "AAPL220318P00182500"
//               ]
//           },
//           "dataSource": "LIVE"
//       },
//       "expirationDates": [
//           "Fri Mar 18 00:00:00 EDT 2022",
//           "Fri Mar 25 00:00:00 EDT 2022"
//       ],
//       "buyOrSellOption": "LONG",
//       "OTM": false,
//       "ITM": false,
//       "optionChainStrategy": {
//           "hugCurrentPrice": {
//               "numOfStrikes": 5
//           }
//       },
//       "optionFilters": {
//           "tightSpreadPct": {
//               "maxOptionSpreadPct": 0.05
//           }
//       }
//   },
//outgoing request expiration dates
// {
//   "key": "optionExpirationDate1647382794947",
//   "symbol": "aapl",
//   "dataSource": "LIVE",
//   "messageType": "optionExpirationDates",
//   "notExpiringToday": false
// }

// incoming request expiration dates
// {
//   "key": "optionExpirationDate1647382794947",
//   "dates": [
//       "Fri Mar 18 00:00:00 EDT 2022",
//       "Fri Mar 25 00:00:00 EDT 2022",
//       "Fri Apr 01 00:00:00 EDT 2022",
//       "Fri Apr 08 00:00:00 EDT 2022",
//       "Thu Apr 14 00:00:00 EDT 2022",
//       "Fri Apr 22 00:00:00 EDT 2022",
//       "Fri Apr 29 00:00:00 EDT 2022",
//       "Fri May 20 00:00:00 EDT 2022",
//       "Fri Jun 17 00:00:00 EDT 2022",
//       "Fri Jul 15 00:00:00 EDT 2022",
//       "Fri Aug 19 00:00:00 EDT 2022",
//       "Fri Sep 16 00:00:00 EDT 2022",
//       "Fri Oct 21 00:00:00 EDT 2022",
//       "Fri Nov 18 00:00:00 EST 2022",
//       "Fri Jan 20 00:00:00 EST 2023",
//       "Fri Mar 17 00:00:00 EDT 2023",
//       "Fri Jun 16 00:00:00 EDT 2023",
//       "Fri Sep 15 00:00:00 EDT 2023",
//       "Fri Jan 19 00:00:00 EST 2024",
//       "Fri Jun 21 00:00:00 EDT 2024"
//   ],
//   "messageType": "optionExpirationDates"
// }

//outgoing request option chains
// {
//   "messageType": "requestOptionChains",
//   "id": 0,
//   "expirationDates": [
//       "Fri Mar 18 00:00:00 EDT 2022",
//       "Fri Mar 25 00:00:00 EDT 2022"
//   ],
//   "symbol": "aapl",
//   "dataSource": "LIVE"
// }

// incoming request option chains
export const exampleRefactoredChainMsg = {
  messageType: 'refactoredRequestOptionChains',
  chains: {
    '2022-03-18': [
      {
        symbol: 'EGHT220318P00001000',
        type: 'option',
        underlying: 'EGHT',
        strike: 1.0,
        last: 0.0,
        expiration_date: '2022-03-18',
        expiration_type: 'standard',
        option_type: 'put',
        root_symbol: 'EGHT',
        greeks: {
          delta: 0.0,
          gamma: -2.74745e-14,
          theta: -0.00185195,
          vega: 2.0e-5,
          rho: 8.23315e-5,
          phi: -8.67704e-4,
          bid_iv: 0.0,
          mid_iv: 0.0,
          ask_iv: 0.0,
          smv_vol: 1.125,
          updated_at: '2022-03-15 19:32:24',
        },
        trade_date: 0,
        volume: 0,
        open_interest: 0,
        bid: 0.0,
        bid_date: 1647373791000,
        ask_date: 1647414000000,
        ask: 0.5,
        optionSymbol: 'EGHT220318P00001000',
      },
      {
        symbol: 'EGHT220318C00001000',
        type: 'option',
        underlying: 'EGHT',
        strike: 1.0,
        last: 0.0,
        expiration_date: '2022-03-18',
        expiration_type: 'standard',
        option_type: 'call',
        root_symbol: 'EGHT',
        greeks: {
          delta: 1.0,
          gamma: -2.74745e-14,
          theta: -0.00185195,
          vega: 2.0e-5,
          rho: 8.23315e-5,
          phi: -8.67704e-4,
          bid_iv: 0.0,
          mid_iv: 0.0,
          ask_iv: 0.0,
          smv_vol: 1.125,
          updated_at: '2022-03-15 19:32:24',
        },
        trade_date: 0,
        volume: 0,
        open_interest: 0,
        bid: 8.8,
        bid_date: 1647414000000,
        ask_date: 1647414000000,
        ask: 10.6,
        optionSymbol: 'EGHT220318C00001000',
      },
      {
        symbol: 'EGHT220318C00002500',
        type: 'option',
        underlying: 'EGHT',
        strike: 2.5,
        last: 0.0,
        expiration_date: '2022-03-18',
        expiration_type: 'standard',
        option_type: 'call',
        root_symbol: 'EGHT',
        greeks: {
          delta: 1.0,
          gamma: -1.60503e-14,
          theta: -0.00462987,
          vega: 2.0e-5,
          rho: 2.05829e-4,
          phi: -8.67704e-4,
          bid_iv: 0.0,
          mid_iv: 0.0,
          ask_iv: 0.0,
          smv_vol: 1.125,
          updated_at: '2022-03-15 19:32:24',
        },
        trade_date: 0,
        volume: 0,
        open_interest: 0,
        bid: 7.9,
        bid_date: 1647414000000,
        ask_date: 1647414000000,
        ask: 8.4,
        optionSymbol: 'EGHT220318C00002500',
      },
      {
        symbol: 'EGHT220318P00002500',
        type: 'option',
        underlying: 'EGHT',
        strike: 2.5,
        last: 0.0,
        expiration_date: '2022-03-18',
        expiration_type: 'standard',
        option_type: 'put',
        root_symbol: 'EGHT',
        greeks: {
          delta: 0.0,
          gamma: -1.60503e-14,
          theta: -0.00462987,
          vega: 2.0e-5,
          rho: 2.05829e-4,
          phi: -8.67704e-4,
          bid_iv: 0.0,
          mid_iv: 0.0,
          ask_iv: 0.0,
          smv_vol: 1.125,
          updated_at: '2022-03-15 19:32:24',
        },
        trade_date: 0,
        volume: 0,
        open_interest: 0,
        bid: 0.0,
        bid_date: 1647373790000,
        ask_date: 1647414000000,
        ask: 0.5,
        optionSymbol: 'EGHT220318P00002500',
      },
      {
        symbol: 'EGHT220318P00004000',
        type: 'option',
        underlying: 'EGHT',
        strike: 4.0,
        last: 0.0,
        expiration_date: '2022-03-18',
        expiration_type: 'standard',
        option_type: 'put',
        root_symbol: 'EGHT',
        greeks: {
          delta: 0.0,
          gamma: 3.47803e-15,
          theta: -0.00740779,
          vega: 2.0e-5,
          rho: 3.29326e-4,
          phi: -8.67704e-4,
          bid_iv: 0.0,
          mid_iv: 0.0,
          ask_iv: 0.0,
          smv_vol: 1.125,
          updated_at: '2022-03-15 19:32:24',
        },
        trade_date: 0,
        volume: 0,
        open_interest: 0,
        bid: 0.0,
        bid_date: 1647373791000,
        ask_date: 1647414000000,
        ask: 0.5,
        optionSymbol: 'EGHT220318P00004000',
      },
      {
        symbol: 'EGHT220318C00004000',
        type: 'option',
        underlying: 'EGHT',
        strike: 4.0,
        last: 0.0,
        expiration_date: '2022-03-18',
        expiration_type: 'standard',
        option_type: 'call',
        root_symbol: 'EGHT',
        greeks: {
          delta: 1.0,
          gamma: 3.47803e-15,
          theta: -0.00740779,
          vega: 2.0e-5,
          rho: 3.29326e-4,
          phi: -8.67704e-4,
          bid_iv: 0.0,
          mid_iv: 0.0,
          ask_iv: 0.0,
          smv_vol: 1.125,
          updated_at: '2022-03-15 19:32:24',
        },
        trade_date: 0,
        volume: 0,
        open_interest: 0,
        bid: 6.1,
        bid_date: 1647414000000,
        ask_date: 1647414000000,
        ask: 7.1,
        optionSymbol: 'EGHT220318C00004000',
      },
      {
        symbol: 'EGHT220318P00005000',
        type: 'option',
        underlying: 'EGHT',
        strike: 5.0,
        last: 0.0,
        expiration_date: '2022-03-18',
        expiration_type: 'standard',
        option_type: 'put',
        root_symbol: 'EGHT',
        greeks: {
          delta: 0.0,
          gamma: 2.91449e-9,
          theta: -0.00925974,
          vega: 2.00001e-5,
          rho: 4.11658e-4,
          phi: -8.67704e-4,
          bid_iv: 0.0,
          mid_iv: 0.0,
          ask_iv: 0.0,
          smv_vol: 1.125,
          updated_at: '2022-03-15 19:32:24',
        },
        trade_date: 0,
        volume: 0,
        open_interest: 0,
        bid: 0.0,
        bid_date: 1647373790000,
        ask_date: 1647414000000,
        ask: 0.5,
        optionSymbol: 'EGHT220318P00005000',
      },
      {
        symbol: 'EGHT220318C00005000',
        type: 'option',
        underlying: 'EGHT',
        strike: 5.0,
        last: 0.0,
        expiration_date: '2022-03-18',
        expiration_type: 'standard',
        option_type: 'call',
        root_symbol: 'EGHT',
        greeks: {
          delta: 1.0,
          gamma: 2.91449e-9,
          theta: -0.00925974,
          vega: 2.00001e-5,
          rho: 4.11658e-4,
          phi: -8.67704e-4,
          bid_iv: 0.0,
          mid_iv: 0.0,
          ask_iv: 0.0,
          smv_vol: 1.125,
          updated_at: '2022-03-15 19:32:24',
        },
        trade_date: 0,
        volume: 0,
        open_interest: 0,
        bid: 4.8,
        bid_date: 1647414000000,
        ask_date: 1647414000000,
        ask: 6.1,
        optionSymbol: 'EGHT220318C00005000',
      },
      {
        symbol: 'EGHT220318C00006000',
        type: 'option',
        underlying: 'EGHT',
        strike: 6.0,
        last: 0.0,
        expiration_date: '2022-03-18',
        expiration_type: 'standard',
        option_type: 'call',
        root_symbol: 'EGHT',
        greeks: {
          delta: 1.0,
          gamma: 4.42622e-7,
          theta: -0.0111118,
          vega: 2.00067e-5,
          rho: 4.93989e-4,
          phi: -8.67704e-4,
          bid_iv: 0.0,
          mid_iv: 0.0,
          ask_iv: 0.0,
          smv_vol: 1.125,
          updated_at: '2022-03-15 19:32:24',
        },
        trade_date: 0,
        volume: 0,
        open_interest: 0,
        bid: 4.1,
        bid_date: 1647414000000,
        ask_date: 1647414000000,
        ask: 5.4,
        optionSymbol: 'EGHT220318C00006000',
      },
    ],
  },
  id: null,
  quote: {
    date: 0,
    symbol: 'AAPL',
    bid: 152.72,
    ask: 152.75,
    last: 153.04,
    size: 64572964,
  },
};

/**
 * 
 * [
    {
        "2022-07-13": [
            {
                "symbol": "AAPL20220713C50",
                "type": "option",
                "underlying": "AAPL",
                "strike": 50,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.01,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.02,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C50",
                "spreadPct": 0.00985221674876848,
                "midBidAsk": 1.0150000000000001
            },
            {
                "symbol": "AAPL20220713P50",
                "type": "option",
                "underlying": "AAPL",
                "strike": 50,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.03,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.04,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P50",
                "spreadPct": 0.009661835748792277,
                "midBidAsk": 1.0350000000000001
            },
            {
                "symbol": "AAPL20220713C51",
                "type": "option",
                "underlying": "AAPL",
                "strike": 51,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.05,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.06,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C51",
                "spreadPct": 0.009478672985781998,
                "midBidAsk": 1.0550000000000002
            },
            {
                "symbol": "AAPL20220713P51",
                "type": "option",
                "underlying": "AAPL",
                "strike": 51,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.07,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.08,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P51",
                "spreadPct": 0.009302325581395356,
                "midBidAsk": 1.0750000000000002
            },
            {
                "symbol": "AAPL20220713C52",
                "type": "option",
                "underlying": "AAPL",
                "strike": 52,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.09,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.1,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C52",
                "spreadPct": 0.009132420091324207,
                "midBidAsk": 1.0950000000000002
            },
            {
                "symbol": "AAPL20220713P52",
                "type": "option",
                "underlying": "AAPL",
                "strike": 52,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.11,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.12,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P52",
                "spreadPct": 0.008968609865470859,
                "midBidAsk": 1.1150000000000002
            },
            {
                "symbol": "AAPL20220713C53",
                "type": "option",
                "underlying": "AAPL",
                "strike": 53,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.1300000000000001,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.1400000000000001,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C53",
                "spreadPct": 0.008810572687224676,
                "midBidAsk": 1.1350000000000002
            },
            {
                "symbol": "AAPL20220713P53",
                "type": "option",
                "underlying": "AAPL",
                "strike": 53,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.1500000000000001,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.1600000000000001,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P53",
                "spreadPct": 0.008658008658008663,
                "midBidAsk": 1.1550000000000002
            },
            {
                "symbol": "AAPL20220713C54",
                "type": "option",
                "underlying": "AAPL",
                "strike": 54,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.1700000000000002,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.1800000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C54",
                "spreadPct": 0.008510638297872346,
                "midBidAsk": 1.1750000000000003
            },
            {
                "symbol": "AAPL20220713P54",
                "type": "option",
                "underlying": "AAPL",
                "strike": 54,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.1900000000000002,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.2000000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P54",
                "spreadPct": 0.00836820083682009,
                "midBidAsk": 1.1950000000000003
            },
            {
                "symbol": "AAPL20220713C55",
                "type": "option",
                "underlying": "AAPL",
                "strike": 55,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.2100000000000002,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.2200000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C55",
                "spreadPct": 0.008230452674897125,
                "midBidAsk": 1.2150000000000003
            },
            {
                "symbol": "AAPL20220713P55",
                "type": "option",
                "underlying": "AAPL",
                "strike": 55,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.2300000000000002,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.2400000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P55",
                "spreadPct": 0.00809716599190284,
                "midBidAsk": 1.2350000000000003
            },
            {
                "symbol": "AAPL20220713C56",
                "type": "option",
                "underlying": "AAPL",
                "strike": 56,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.2500000000000002,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.2600000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C56",
                "spreadPct": 0.007968127490039846,
                "midBidAsk": 1.2550000000000003
            },
            {
                "symbol": "AAPL20220713P56",
                "type": "option",
                "underlying": "AAPL",
                "strike": 56,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.2700000000000002,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.2800000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P56",
                "spreadPct": 0.007843137254901966,
                "midBidAsk": 1.2750000000000004
            },
            {
                "symbol": "AAPL20220713C57",
                "type": "option",
                "underlying": "AAPL",
                "strike": 57,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.2900000000000003,
                "bid_date": 1657753555783,
                "ask_date": 1657753555783,
                "ask": 1.3000000000000003,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C57",
                "spreadPct": 0.0077220077220077265,
                "midBidAsk": 1.2950000000000004
            },
            {
                "symbol": "AAPL20220713P57",
                "type": "option",
                "underlying": "AAPL",
                "strike": 57,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.3100000000000003,
                "bid_date": 1657753555784,
                "ask_date": 1657753555783,
                "ask": 1.3200000000000003,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.783-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P57",
                "spreadPct": 0.00760456273764259,
                "midBidAsk": 1.3150000000000004
            },
            {
                "symbol": "AAPL20220713C58",
                "type": "option",
                "underlying": "AAPL",
                "strike": 58,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.3300000000000003,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.3400000000000003,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C58",
                "spreadPct": 0.007490636704119855,
                "midBidAsk": 1.3350000000000004
            },
            {
                "symbol": "AAPL20220713P58",
                "type": "option",
                "underlying": "AAPL",
                "strike": 58,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.3500000000000003,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.3600000000000003,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P58",
                "spreadPct": 0.0073800738007380115,
                "midBidAsk": 1.3550000000000004
            },
            {
                "symbol": "AAPL20220713C59",
                "type": "option",
                "underlying": "AAPL",
                "strike": 59,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.3700000000000003,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.3800000000000003,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C59",
                "spreadPct": 0.007272727272727277,
                "midBidAsk": 1.3750000000000004
            },
            {
                "symbol": "AAPL20220713P59",
                "type": "option",
                "underlying": "AAPL",
                "strike": 59,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.3900000000000003,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.4000000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P59",
                "spreadPct": 0.0071684587813620115,
                "midBidAsk": 1.3950000000000005
            },
            {
                "symbol": "AAPL20220713C60",
                "type": "option",
                "underlying": "AAPL",
                "strike": 60,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.4100000000000004,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.4200000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C60",
                "spreadPct": 0.007067137809187283,
                "midBidAsk": 1.4150000000000005
            },
            {
                "symbol": "AAPL20220713P60",
                "type": "option",
                "underlying": "AAPL",
                "strike": 60,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.4300000000000004,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.4400000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P60",
                "spreadPct": 0.006968641114982582,
                "midBidAsk": 1.4350000000000005
            },
            {
                "symbol": "AAPL20220713C61",
                "type": "option",
                "underlying": "AAPL",
                "strike": 61,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.4500000000000004,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.4600000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C61",
                "spreadPct": 0.006872852233676979,
                "midBidAsk": 1.4550000000000005
            },
            {
                "symbol": "AAPL20220713P61",
                "type": "option",
                "underlying": "AAPL",
                "strike": 61,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.4700000000000004,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.4800000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P61",
                "spreadPct": 0.006779661016949156,
                "midBidAsk": 1.4750000000000005
            },
            {
                "symbol": "AAPL20220713C62",
                "type": "option",
                "underlying": "AAPL",
                "strike": 62,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.4900000000000004,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.5000000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C62",
                "spreadPct": 0.0066889632107023445,
                "midBidAsk": 1.4950000000000006
            },
            {
                "symbol": "AAPL20220713P62",
                "type": "option",
                "underlying": "AAPL",
                "strike": 62,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.5100000000000005,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.5200000000000005,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P62",
                "spreadPct": 0.006600660066006604,
                "midBidAsk": 1.5150000000000006
            },
            {
                "symbol": "AAPL20220713C63",
                "type": "option",
                "underlying": "AAPL",
                "strike": 63,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.5300000000000005,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.5400000000000005,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C63",
                "spreadPct": 0.006514657980456029,
                "midBidAsk": 1.5350000000000006
            },
            {
                "symbol": "AAPL20220713P63",
                "type": "option",
                "underlying": "AAPL",
                "strike": 63,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.5500000000000005,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.5600000000000005,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P63",
                "spreadPct": 0.006430868167202576,
                "midBidAsk": 1.5550000000000006
            },
            {
                "symbol": "AAPL20220713C64",
                "type": "option",
                "underlying": "AAPL",
                "strike": 64,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.5700000000000005,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.5800000000000005,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C64",
                "spreadPct": 0.006349206349206353,
                "midBidAsk": 1.5750000000000006
            },
            {
                "symbol": "AAPL20220713P64",
                "type": "option",
                "underlying": "AAPL",
                "strike": 64,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.5900000000000005,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.6000000000000005,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P64",
                "spreadPct": 0.0062695924764890314,
                "midBidAsk": 1.5950000000000006
            },
            {
                "symbol": "AAPL20220713C65",
                "type": "option",
                "underlying": "AAPL",
                "strike": 65,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.6100000000000005,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.6200000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C65",
                "spreadPct": 0.006191950464396288,
                "midBidAsk": 1.6150000000000007
            },
            {
                "symbol": "AAPL20220713P65",
                "type": "option",
                "underlying": "AAPL",
                "strike": 65,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.6300000000000006,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.6400000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P65",
                "spreadPct": 0.006116207951070339,
                "midBidAsk": 1.6350000000000007
            },
            {
                "symbol": "AAPL20220713C66",
                "type": "option",
                "underlying": "AAPL",
                "strike": 66,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.6500000000000006,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.6600000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C66",
                "spreadPct": 0.0060422960725075555,
                "midBidAsk": 1.6550000000000007
            },
            {
                "symbol": "AAPL20220713P66",
                "type": "option",
                "underlying": "AAPL",
                "strike": 66,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.6700000000000006,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.6800000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P66",
                "spreadPct": 0.005970149253731346,
                "midBidAsk": 1.6750000000000007
            },
            {
                "symbol": "AAPL20220713C67",
                "type": "option",
                "underlying": "AAPL",
                "strike": 67,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.6900000000000006,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.7000000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C67",
                "spreadPct": 0.005899705014749265,
                "midBidAsk": 1.6950000000000007
            },
            {
                "symbol": "AAPL20220713P67",
                "type": "option",
                "underlying": "AAPL",
                "strike": 67,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.7100000000000006,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.7200000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P67",
                "spreadPct": 0.005830903790087466,
                "midBidAsk": 1.7150000000000007
            },
            {
                "symbol": "AAPL20220713C68",
                "type": "option",
                "underlying": "AAPL",
                "strike": 68,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.7300000000000006,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.7400000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C68",
                "spreadPct": 0.005763688760806919,
                "midBidAsk": 1.7350000000000008
            },
            {
                "symbol": "AAPL20220713P68",
                "type": "option",
                "underlying": "AAPL",
                "strike": 68,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.7500000000000007,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.7600000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P68",
                "spreadPct": 0.005698005698005701,
                "midBidAsk": 1.7550000000000008
            },
            {
                "symbol": "AAPL20220713C69",
                "type": "option",
                "underlying": "AAPL",
                "strike": 69,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.7700000000000007,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.7800000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C69",
                "spreadPct": 0.0056338028169014105,
                "midBidAsk": 1.7750000000000008
            },
            {
                "symbol": "AAPL20220713P69",
                "type": "option",
                "underlying": "AAPL",
                "strike": 69,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.7900000000000007,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.8000000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P69",
                "spreadPct": 0.005571030640668526,
                "midBidAsk": 1.7950000000000008
            },
            {
                "symbol": "AAPL20220713C70",
                "type": "option",
                "underlying": "AAPL",
                "strike": 70,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.8100000000000007,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.8200000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C70",
                "spreadPct": 0.00550964187327824,
                "midBidAsk": 1.8150000000000008
            },
            {
                "symbol": "AAPL20220713P70",
                "type": "option",
                "underlying": "AAPL",
                "strike": 70,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.8300000000000007,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.8400000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P70",
                "spreadPct": 0.005449591280653953,
                "midBidAsk": 1.8350000000000009
            },
            {
                "symbol": "AAPL20220713C71",
                "type": "option",
                "underlying": "AAPL",
                "strike": 71,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.8500000000000008,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.8600000000000008,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C71",
                "spreadPct": 0.005390835579514827,
                "midBidAsk": 1.8550000000000009
            },
            {
                "symbol": "AAPL20220713P71",
                "type": "option",
                "underlying": "AAPL",
                "strike": 71,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.8700000000000008,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.8800000000000008,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P71",
                "spreadPct": 0.005333333333333336,
                "midBidAsk": 1.8750000000000009
            },
            {
                "symbol": "AAPL20220713C72",
                "type": "option",
                "underlying": "AAPL",
                "strike": 72,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.8900000000000008,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.9000000000000008,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C72",
                "spreadPct": 0.005277044854881269,
                "midBidAsk": 1.895000000000001
            },
            {
                "symbol": "AAPL20220713P72",
                "type": "option",
                "underlying": "AAPL",
                "strike": 72,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.9100000000000008,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.9200000000000008,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P72",
                "spreadPct": 0.005221932114882508,
                "midBidAsk": 1.915000000000001
            },
            {
                "symbol": "AAPL20220713C73",
                "type": "option",
                "underlying": "AAPL",
                "strike": 73,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.9300000000000008,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.9400000000000008,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C73",
                "spreadPct": 0.005167958656330751,
                "midBidAsk": 1.935000000000001
            },
            {
                "symbol": "AAPL20220713P73",
                "type": "option",
                "underlying": "AAPL",
                "strike": 73,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.9500000000000008,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.9600000000000009,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P73",
                "spreadPct": 0.005115089514066498,
                "midBidAsk": 1.955000000000001
            },
            {
                "symbol": "AAPL20220713C74",
                "type": "option",
                "underlying": "AAPL",
                "strike": 74,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.9700000000000009,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 1.9800000000000009,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C74",
                "spreadPct": 0.005063291139240508,
                "midBidAsk": 1.975000000000001
            },
            {
                "symbol": "AAPL20220713P74",
                "type": "option",
                "underlying": "AAPL",
                "strike": 74,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.9900000000000009,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.000000000000001,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P74",
                "spreadPct": 0.005012531328320804,
                "midBidAsk": 1.995000000000001
            },
            {
                "symbol": "AAPL20220713C75",
                "type": "option",
                "underlying": "AAPL",
                "strike": 75,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.010000000000001,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.020000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C75",
                "spreadPct": 0.004962779156327435,
                "midBidAsk": 2.015000000000001
            },
            {
                "symbol": "AAPL20220713P75",
                "type": "option",
                "underlying": "AAPL",
                "strike": 75,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.030000000000001,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.040000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P75",
                "spreadPct": 0.004914004914004807,
                "midBidAsk": 2.035000000000001
            },
            {
                "symbol": "AAPL20220713C76",
                "type": "option",
                "underlying": "AAPL",
                "strike": 76,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.050000000000001,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.060000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C76",
                "spreadPct": 0.004866180048661694,
                "midBidAsk": 2.055000000000001
            },
            {
                "symbol": "AAPL20220713P76",
                "type": "option",
                "underlying": "AAPL",
                "strike": 76,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.070000000000001,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.080000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P76",
                "spreadPct": 0.0048192771084336295,
                "midBidAsk": 2.075000000000001
            },
            {
                "symbol": "AAPL20220713C77",
                "type": "option",
                "underlying": "AAPL",
                "strike": 77,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.090000000000001,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.100000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C77",
                "spreadPct": 0.004773269689737366,
                "midBidAsk": 2.095000000000001
            },
            {
                "symbol": "AAPL20220713P77",
                "type": "option",
                "underlying": "AAPL",
                "strike": 77,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.110000000000001,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.120000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P77",
                "spreadPct": 0.0047281323877067525,
                "midBidAsk": 2.115000000000001
            },
            {
                "symbol": "AAPL20220713C78",
                "type": "option",
                "underlying": "AAPL",
                "strike": 78,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.1300000000000012,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.140000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C78",
                "spreadPct": 0.0046838407494144176,
                "midBidAsk": 2.135000000000001
            },
            {
                "symbol": "AAPL20220713P78",
                "type": "option",
                "underlying": "AAPL",
                "strike": 78,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.1500000000000012,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.160000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P78",
                "spreadPct": 0.004640371229698274,
                "midBidAsk": 2.155000000000001
            },
            {
                "symbol": "AAPL20220713C79",
                "type": "option",
                "underlying": "AAPL",
                "strike": 79,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.1700000000000013,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.180000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C79",
                "spreadPct": 0.004597701149425187,
                "midBidAsk": 2.175000000000001
            },
            {
                "symbol": "AAPL20220713P79",
                "type": "option",
                "underlying": "AAPL",
                "strike": 79,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.1900000000000013,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.200000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P79",
                "spreadPct": 0.0045558086560363465,
                "midBidAsk": 2.195000000000001
            },
            {
                "symbol": "AAPL20220713C80",
                "type": "option",
                "underlying": "AAPL",
                "strike": 80,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.2100000000000013,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.220000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C80",
                "spreadPct": 0.00451467268623015,
                "midBidAsk": 2.215000000000001
            },
            {
                "symbol": "AAPL20220713P80",
                "type": "option",
                "underlying": "AAPL",
                "strike": 80,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.2300000000000013,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.240000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P80",
                "spreadPct": 0.0044742729306486715,
                "midBidAsk": 2.235000000000001
            },
            {
                "symbol": "AAPL20220713C81",
                "type": "option",
                "underlying": "AAPL",
                "strike": 81,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.2500000000000013,
                "bid_date": 1657753555784,
                "ask_date": 1657753555784,
                "ask": 2.260000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.784-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C81",
                "spreadPct": 0.004434589800443362,
                "midBidAsk": 2.2550000000000012
            },
            {
                "symbol": "AAPL20220713P81",
                "type": "option",
                "underlying": "AAPL",
                "strike": 81,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.2700000000000014,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.280000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P81",
                "spreadPct": 0.004395604395604299,
                "midBidAsk": 2.2750000000000012
            },
            {
                "symbol": "AAPL20220713C82",
                "type": "option",
                "underlying": "AAPL",
                "strike": 82,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.2900000000000014,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.300000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C82",
                "spreadPct": 0.004357298474945439,
                "midBidAsk": 2.2950000000000013
            },
            {
                "symbol": "AAPL20220713P82",
                "type": "option",
                "underlying": "AAPL",
                "strike": 82,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.3100000000000014,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.320000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P82",
                "spreadPct": 0.004319654427645694,
                "midBidAsk": 2.3150000000000013
            },
            {
                "symbol": "AAPL20220713C83",
                "type": "option",
                "underlying": "AAPL",
                "strike": 83,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.3300000000000014,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.340000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C83",
                "spreadPct": 0.004282655246252583,
                "midBidAsk": 2.3350000000000013
            },
            {
                "symbol": "AAPL20220713P83",
                "type": "option",
                "underlying": "AAPL",
                "strike": 83,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.3500000000000014,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.360000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P83",
                "spreadPct": 0.004246284501061478,
                "midBidAsk": 2.3550000000000013
            },
            {
                "symbol": "AAPL20220713C84",
                "type": "option",
                "underlying": "AAPL",
                "strike": 84,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.3700000000000014,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.3800000000000012,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C84",
                "spreadPct": 0.004210526315789382,
                "midBidAsk": 2.3750000000000013
            },
            {
                "symbol": "AAPL20220713P84",
                "type": "option",
                "underlying": "AAPL",
                "strike": 84,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.3900000000000015,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.4000000000000012,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P84",
                "spreadPct": 0.004175365344467549,
                "midBidAsk": 2.3950000000000014
            },
            {
                "symbol": "AAPL20220713C85",
                "type": "option",
                "underlying": "AAPL",
                "strike": 85,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.4100000000000015,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.4200000000000013,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C85",
                "spreadPct": 0.004140786749482311,
                "midBidAsk": 2.4150000000000014
            },
            {
                "symbol": "AAPL20220713P85",
                "type": "option",
                "underlying": "AAPL",
                "strike": 85,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.4300000000000015,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.4400000000000013,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P85",
                "spreadPct": 0.004106776180698062,
                "midBidAsk": 2.4350000000000014
            },
            {
                "symbol": "AAPL20220713C86",
                "type": "option",
                "underlying": "AAPL",
                "strike": 86,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.4500000000000015,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.4600000000000013,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C86",
                "spreadPct": 0.004073319755600726,
                "midBidAsk": 2.4550000000000014
            },
            {
                "symbol": "AAPL20220713P86",
                "type": "option",
                "underlying": "AAPL",
                "strike": 86,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.4700000000000015,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.4800000000000013,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P86",
                "spreadPct": 0.004040404040403952,
                "midBidAsk": 2.4750000000000014
            },
            {
                "symbol": "AAPL20220713C87",
                "type": "option",
                "underlying": "AAPL",
                "strike": 87,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.4900000000000015,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.5000000000000013,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C87",
                "spreadPct": 0.00400801603206404,
                "midBidAsk": 2.4950000000000014
            },
            {
                "symbol": "AAPL20220713P87",
                "type": "option",
                "underlying": "AAPL",
                "strike": 87,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.5100000000000016,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.5200000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P87",
                "spreadPct": 0.003976143141152994,
                "midBidAsk": 2.5150000000000015
            },
            {
                "symbol": "AAPL20220713C88",
                "type": "option",
                "underlying": "AAPL",
                "strike": 88,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.5300000000000016,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.5400000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C88",
                "spreadPct": 0.00394477317554232,
                "midBidAsk": 2.5350000000000015
            },
            {
                "symbol": "AAPL20220713P88",
                "type": "option",
                "underlying": "AAPL",
                "strike": 88,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.5500000000000016,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.5600000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P88",
                "spreadPct": 0.003913894324853143,
                "midBidAsk": 2.5550000000000015
            },
            {
                "symbol": "AAPL20220713C89",
                "type": "option",
                "underlying": "AAPL",
                "strike": 89,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.5700000000000016,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.5800000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C89",
                "spreadPct": 0.003883495145630983,
                "midBidAsk": 2.5750000000000015
            },
            {
                "symbol": "AAPL20220713P89",
                "type": "option",
                "underlying": "AAPL",
                "strike": 89,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.5900000000000016,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.6000000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P89",
                "spreadPct": 0.003853564547206081,
                "midBidAsk": 2.5950000000000015
            },
            {
                "symbol": "AAPL20220713C90",
                "type": "option",
                "underlying": "AAPL",
                "strike": 90,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.6100000000000017,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.6200000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C90",
                "spreadPct": 0.003824091778202593,
                "midBidAsk": 2.6150000000000015
            },
            {
                "symbol": "AAPL20220713P90",
                "type": "option",
                "underlying": "AAPL",
                "strike": 90,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.6300000000000017,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.6400000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P90",
                "spreadPct": 0.003795066413662156,
                "midBidAsk": 2.6350000000000016
            },
            {
                "symbol": "AAPL20220713C91",
                "type": "option",
                "underlying": "AAPL",
                "strike": 91,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.6500000000000017,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.6600000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C91",
                "spreadPct": 0.0037664783427494466,
                "midBidAsk": 2.6550000000000016
            },
            {
                "symbol": "AAPL20220713P91",
                "type": "option",
                "underlying": "AAPL",
                "strike": 91,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.6700000000000017,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.6800000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P91",
                "spreadPct": 0.003738317757009264,
                "midBidAsk": 2.6750000000000016
            },
            {
                "symbol": "AAPL20220713C92",
                "type": "option",
                "underlying": "AAPL",
                "strike": 92,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.6900000000000017,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.7000000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C92",
                "spreadPct": 0.0037105751391464862,
                "midBidAsk": 2.6950000000000016
            },
            {
                "symbol": "AAPL20220713P92",
                "type": "option",
                "underlying": "AAPL",
                "strike": 92,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.7100000000000017,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.7200000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P92",
                "spreadPct": 0.0036832412523019453,
                "midBidAsk": 2.7150000000000016
            },
            {
                "symbol": "AAPL20220713C93",
                "type": "option",
                "underlying": "AAPL",
                "strike": 93,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.7300000000000018,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.7400000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C93",
                "spreadPct": 0.003656307129798823,
                "midBidAsk": 2.7350000000000017
            },
            {
                "symbol": "AAPL20220713P93",
                "type": "option",
                "underlying": "AAPL",
                "strike": 93,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.7500000000000018,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.7600000000000016,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P93",
                "spreadPct": 0.0036297640653356737,
                "midBidAsk": 2.7550000000000017
            },
            {
                "symbol": "AAPL20220713C94",
                "type": "option",
                "underlying": "AAPL",
                "strike": 94,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.770000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.7800000000000016,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C94",
                "spreadPct": 0.0036036036036035247,
                "midBidAsk": 2.7750000000000017
            },
            {
                "symbol": "AAPL20220713P94",
                "type": "option",
                "underlying": "AAPL",
                "strike": 94,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.790000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.8000000000000016,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P94",
                "spreadPct": 0.003577817531305825,
                "midBidAsk": 2.7950000000000017
            },
            {
                "symbol": "AAPL20220713C95",
                "type": "option",
                "underlying": "AAPL",
                "strike": 95,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.810000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.8200000000000016,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C95",
                "spreadPct": 0.003552397868561201,
                "midBidAsk": 2.8150000000000017
            },
            {
                "symbol": "AAPL20220713P95",
                "type": "option",
                "underlying": "AAPL",
                "strike": 95,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.830000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.8400000000000016,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P95",
                "spreadPct": 0.0035273368606701166,
                "midBidAsk": 2.8350000000000017
            },
            {
                "symbol": "AAPL20220713C96",
                "type": "option",
                "underlying": "AAPL",
                "strike": 96,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.850000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.8600000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C96",
                "spreadPct": 0.003502626970227594,
                "midBidAsk": 2.8550000000000018
            },
            {
                "symbol": "AAPL20220713P96",
                "type": "option",
                "underlying": "AAPL",
                "strike": 96,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.870000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.8800000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P96",
                "spreadPct": 0.003478260869565141,
                "midBidAsk": 2.8750000000000018
            },
            {
                "symbol": "AAPL20220713C97",
                "type": "option",
                "underlying": "AAPL",
                "strike": 97,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.890000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.9000000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C97",
                "spreadPct": 0.0034542314335059693,
                "midBidAsk": 2.895000000000002
            },
            {
                "symbol": "AAPL20220713P97",
                "type": "option",
                "underlying": "AAPL",
                "strike": 97,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.910000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.9200000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P97",
                "spreadPct": 0.00343053173241845,
                "midBidAsk": 2.915000000000002
            },
            {
                "symbol": "AAPL20220713C98",
                "type": "option",
                "underlying": "AAPL",
                "strike": 98,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.930000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.9400000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C98",
                "spreadPct": 0.003407155025553588,
                "midBidAsk": 2.935000000000002
            },
            {
                "symbol": "AAPL20220713P98",
                "type": "option",
                "underlying": "AAPL",
                "strike": 98,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.950000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.9600000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P98",
                "spreadPct": 0.003384094754653056,
                "midBidAsk": 2.955000000000002
            },
            {
                "symbol": "AAPL20220713C99",
                "type": "option",
                "underlying": "AAPL",
                "strike": 99,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.970000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 2.9800000000000018,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C99",
                "spreadPct": 0.003361344537815052,
                "midBidAsk": 2.975000000000002
            },
            {
                "symbol": "AAPL20220713P99",
                "type": "option",
                "underlying": "AAPL",
                "strike": 99,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.990000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.0000000000000018,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P99",
                "spreadPct": 0.003338898163605937,
                "midBidAsk": 2.995000000000002
            },
            {
                "symbol": "AAPL20220713C100",
                "type": "option",
                "underlying": "AAPL",
                "strike": 100,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.010000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.020000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C100",
                "spreadPct": 0.003316749585406229,
                "midBidAsk": 3.015000000000002
            },
            {
                "symbol": "AAPL20220713P100",
                "type": "option",
                "underlying": "AAPL",
                "strike": 100,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.030000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.040000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P100",
                "spreadPct": 0.0032948929159801583,
                "midBidAsk": 3.035000000000002
            },
            {
                "symbol": "AAPL20220713C101",
                "type": "option",
                "underlying": "AAPL",
                "strike": 101,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.050000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.060000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C101",
                "spreadPct": 0.0032733224222585206,
                "midBidAsk": 3.055000000000002
            },
            {
                "symbol": "AAPL20220713P101",
                "type": "option",
                "underlying": "AAPL",
                "strike": 101,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.070000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.080000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P101",
                "spreadPct": 0.0032520325203251317,
                "midBidAsk": 3.075000000000002
            },
            {
                "symbol": "AAPL20220713C102",
                "type": "option",
                "underlying": "AAPL",
                "strike": 102,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.090000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.100000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C102",
                "spreadPct": 0.0032310177705976674,
                "midBidAsk": 3.095000000000002
            },
            {
                "symbol": "AAPL20220713P102",
                "type": "option",
                "underlying": "AAPL",
                "strike": 102,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.110000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.120000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P102",
                "spreadPct": 0.0032102728731941512,
                "midBidAsk": 3.115000000000002
            },
            {
                "symbol": "AAPL20220713C103",
                "type": "option",
                "underlying": "AAPL",
                "strike": 103,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.130000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.140000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C103",
                "spreadPct": 0.0031897926634768038,
                "midBidAsk": 3.135000000000002
            },
            {
                "symbol": "AAPL20220713P103",
                "type": "option",
                "underlying": "AAPL",
                "strike": 103,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.150000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.160000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P103",
                "spreadPct": 0.003169572107765382,
                "midBidAsk": 3.155000000000002
            },
            {
                "symbol": "AAPL20220713C104",
                "type": "option",
                "underlying": "AAPL",
                "strike": 104,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.170000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.180000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C104",
                "spreadPct": 0.0031496062992125294,
                "midBidAsk": 3.175000000000002
            },
            {
                "symbol": "AAPL20220713P104",
                "type": "option",
                "underlying": "AAPL",
                "strike": 104,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.190000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.200000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P104",
                "spreadPct": 0.003129890453834047,
                "midBidAsk": 3.195000000000002
            },
            {
                "symbol": "AAPL20220713C105",
                "type": "option",
                "underlying": "AAPL",
                "strike": 105,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.210000000000002,
                "bid_date": 1657753555785,
                "ask_date": 1657753555785,
                "ask": 3.220000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C105",
                "spreadPct": 0.0031104199066873343,
                "midBidAsk": 3.215000000000002
            },
            {
                "symbol": "AAPL20220713P105",
                "type": "option",
                "underlying": "AAPL",
                "strike": 105,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.230000000000002,
                "bid_date": 1657753555786,
                "ask_date": 1657753555785,
                "ask": 3.240000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.785-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P105",
                "spreadPct": 0.003091190108191586,
                "midBidAsk": 3.235000000000002
            },
            {
                "symbol": "AAPL20220713C106",
                "type": "option",
                "underlying": "AAPL",
                "strike": 106,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.250000000000002,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.260000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C106",
                "spreadPct": 0.0030721966205836497,
                "midBidAsk": 3.255000000000002
            },
            {
                "symbol": "AAPL20220713P106",
                "type": "option",
                "underlying": "AAPL",
                "strike": 106,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.2700000000000022,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.280000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P106",
                "spreadPct": 0.0030534351145037495,
                "midBidAsk": 3.275000000000002
            },
            {
                "symbol": "AAPL20220713C107",
                "type": "option",
                "underlying": "AAPL",
                "strike": 107,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.2900000000000023,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.300000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C107",
                "spreadPct": 0.003034901365705548,
                "midBidAsk": 3.295000000000002
            },
            {
                "symbol": "AAPL20220713P107",
                "type": "option",
                "underlying": "AAPL",
                "strike": 107,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.3100000000000023,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.320000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P107",
                "spreadPct": 0.0030165912518853033,
                "midBidAsk": 3.315000000000002
            },
            {
                "symbol": "AAPL20220713C108",
                "type": "option",
                "underlying": "AAPL",
                "strike": 108,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.3300000000000023,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.340000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C108",
                "spreadPct": 0.0029985007496251214,
                "midBidAsk": 3.335000000000002
            },
            {
                "symbol": "AAPL20220713P108",
                "type": "option",
                "underlying": "AAPL",
                "strike": 108,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.3500000000000023,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.360000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P108",
                "spreadPct": 0.002980625931445538,
                "midBidAsk": 3.355000000000002
            },
            {
                "symbol": "AAPL20220713C109",
                "type": "option",
                "underlying": "AAPL",
                "strike": 109,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.3700000000000023,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.380000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C109",
                "spreadPct": 0.0029629629629628978,
                "midBidAsk": 3.375000000000002
            },
            {
                "symbol": "AAPL20220713P109",
                "type": "option",
                "underlying": "AAPL",
                "strike": 109,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.3900000000000023,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.400000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P109",
                "spreadPct": 0.0029455081001472107,
                "midBidAsk": 3.3950000000000022
            },
            {
                "symbol": "AAPL20220713C110",
                "type": "option",
                "underlying": "AAPL",
                "strike": 110,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.4100000000000024,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.420000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C110",
                "spreadPct": 0.002928257686676363,
                "midBidAsk": 3.4150000000000023
            },
            {
                "symbol": "AAPL20220713P110",
                "type": "option",
                "underlying": "AAPL",
                "strike": 110,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.4300000000000024,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.440000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P110",
                "spreadPct": 0.00291120815138276,
                "midBidAsk": 3.4350000000000023
            },
            {
                "symbol": "AAPL20220713C111",
                "type": "option",
                "underlying": "AAPL",
                "strike": 111,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.4500000000000024,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.460000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C111",
                "spreadPct": 0.0028943560057886485,
                "midBidAsk": 3.4550000000000023
            },
            {
                "symbol": "AAPL20220713P111",
                "type": "option",
                "underlying": "AAPL",
                "strike": 111,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.4700000000000024,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.480000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P111",
                "spreadPct": 0.0028776978417265555,
                "midBidAsk": 3.4750000000000023
            },
            {
                "symbol": "AAPL20220713C112",
                "type": "option",
                "underlying": "AAPL",
                "strike": 112,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.4900000000000024,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.500000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C112",
                "spreadPct": 0.002861230329041425,
                "midBidAsk": 3.4950000000000023
            },
            {
                "symbol": "AAPL20220713P112",
                "type": "option",
                "underlying": "AAPL",
                "strike": 112,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.5100000000000025,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.5200000000000022,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P112",
                "spreadPct": 0.0028449502133712033,
                "midBidAsk": 3.5150000000000023
            },
            {
                "symbol": "AAPL20220713C113",
                "type": "option",
                "underlying": "AAPL",
                "strike": 113,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.5300000000000025,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.5400000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C113",
                "spreadPct": 0.0028288543140027665,
                "midBidAsk": 3.5350000000000024
            },
            {
                "symbol": "AAPL20220713P113",
                "type": "option",
                "underlying": "AAPL",
                "strike": 113,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.5500000000000025,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.5600000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P113",
                "spreadPct": 0.0028129395218002193,
                "midBidAsk": 3.5550000000000024
            },
            {
                "symbol": "AAPL20220713C114",
                "type": "option",
                "underlying": "AAPL",
                "strike": 114,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.5700000000000025,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.5800000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C114",
                "spreadPct": 0.0027972027972027356,
                "midBidAsk": 3.5750000000000024
            },
            {
                "symbol": "AAPL20220713P114",
                "type": "option",
                "underlying": "AAPL",
                "strike": 114,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.5900000000000025,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.6000000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P114",
                "spreadPct": 0.0027816411682892294,
                "midBidAsk": 3.5950000000000024
            },
            {
                "symbol": "AAPL20220713C115",
                "type": "option",
                "underlying": "AAPL",
                "strike": 115,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.6100000000000025,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.6200000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C115",
                "spreadPct": 0.0027662517289072696,
                "midBidAsk": 3.6150000000000024
            },
            {
                "symbol": "AAPL20220713P115",
                "type": "option",
                "underlying": "AAPL",
                "strike": 115,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.6300000000000026,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.6400000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P115",
                "spreadPct": 0.0027510316368637633,
                "midBidAsk": 3.6350000000000025
            },
            {
                "symbol": "AAPL20220713C116",
                "type": "option",
                "underlying": "AAPL",
                "strike": 116,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.6500000000000026,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.6600000000000024,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C116",
                "spreadPct": 0.0027359781121750423,
                "midBidAsk": 3.6550000000000025
            },
            {
                "symbol": "AAPL20220713P116",
                "type": "option",
                "underlying": "AAPL",
                "strike": 116,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.6700000000000026,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.6800000000000024,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P116",
                "spreadPct": 0.0027210884353740896,
                "midBidAsk": 3.6750000000000025
            },
            {
                "symbol": "AAPL20220713C117",
                "type": "option",
                "underlying": "AAPL",
                "strike": 117,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.6900000000000026,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.7000000000000024,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C117",
                "spreadPct": 0.0027063599458727414,
                "midBidAsk": 3.6950000000000025
            },
            {
                "symbol": "AAPL20220713P117",
                "type": "option",
                "underlying": "AAPL",
                "strike": 117,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.7100000000000026,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.7200000000000024,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P117",
                "spreadPct": 0.002691790040376791,
                "midBidAsk": 3.7150000000000025
            },
            {
                "symbol": "AAPL20220713C118",
                "type": "option",
                "underlying": "AAPL",
                "strike": 118,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.7300000000000026,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.7400000000000024,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C118",
                "spreadPct": 0.002677376171352016,
                "midBidAsk": 3.7350000000000025
            },
            {
                "symbol": "AAPL20220713P118",
                "type": "option",
                "underlying": "AAPL",
                "strike": 118,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.7500000000000027,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.7600000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P118",
                "spreadPct": 0.0026631158455392226,
                "midBidAsk": 3.7550000000000026
            },
            {
                "symbol": "AAPL20220713C119",
                "type": "option",
                "underlying": "AAPL",
                "strike": 119,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.7700000000000027,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.7800000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C119",
                "spreadPct": 0.002649006622516498,
                "midBidAsk": 3.7750000000000026
            },
            {
                "symbol": "AAPL20220713P119",
                "type": "option",
                "underlying": "AAPL",
                "strike": 119,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.7900000000000027,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.8000000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P119",
                "spreadPct": 0.002635046113306925,
                "midBidAsk": 3.7950000000000026
            },
            {
                "symbol": "AAPL20220713C120",
                "type": "option",
                "underlying": "AAPL",
                "strike": 120,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.8100000000000027,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.8200000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C120",
                "spreadPct": 0.0026212319790300865,
                "midBidAsk": 3.8150000000000026
            },
            {
                "symbol": "AAPL20220713P120",
                "type": "option",
                "underlying": "AAPL",
                "strike": 120,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.8300000000000027,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.8400000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P120",
                "spreadPct": 0.0026075619295957706,
                "midBidAsk": 3.8350000000000026
            },
            {
                "symbol": "AAPL20220713C121",
                "type": "option",
                "underlying": "AAPL",
                "strike": 121,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.8500000000000028,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.8600000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C121",
                "spreadPct": 0.0025940337224383348,
                "midBidAsk": 3.8550000000000026
            },
            {
                "symbol": "AAPL20220713P121",
                "type": "option",
                "underlying": "AAPL",
                "strike": 121,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.8700000000000028,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.8800000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P121",
                "spreadPct": 0.0025806451612902658,
                "midBidAsk": 3.8750000000000027
            },
            {
                "symbol": "AAPL20220713C122",
                "type": "option",
                "underlying": "AAPL",
                "strike": 122,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.890000000000003,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.9000000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C122",
                "spreadPct": 0.002567394094993525,
                "midBidAsk": 3.8950000000000027
            },
            {
                "symbol": "AAPL20220713P122",
                "type": "option",
                "underlying": "AAPL",
                "strike": 122,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.910000000000003,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.9200000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P122",
                "spreadPct": 0.0025542784163473256,
                "midBidAsk": 3.9150000000000027
            },
            {
                "symbol": "AAPL20220713C123",
                "type": "option",
                "underlying": "AAPL",
                "strike": 123,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.930000000000003,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.9400000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C123",
                "spreadPct": 0.0025412960609910496,
                "midBidAsk": 3.9350000000000027
            },
            {
                "symbol": "AAPL20220713P123",
                "type": "option",
                "underlying": "AAPL",
                "strike": 123,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.950000000000003,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.9600000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P123",
                "spreadPct": 0.002528445006321057,
                "midBidAsk": 3.9550000000000027
            },
            {
                "symbol": "AAPL20220713C124",
                "type": "option",
                "underlying": "AAPL",
                "strike": 124,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.970000000000003,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 3.9800000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C124",
                "spreadPct": 0.0025157232704401964,
                "midBidAsk": 3.9750000000000028
            },
            {
                "symbol": "AAPL20220713P124",
                "type": "option",
                "underlying": "AAPL",
                "strike": 124,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.990000000000003,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 4.000000000000003,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P124",
                "spreadPct": 0.0025031289111388686,
                "midBidAsk": 3.9950000000000028
            },
            {
                "symbol": "AAPL20220713C125",
                "type": "option",
                "underlying": "AAPL",
                "strike": 125,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.0100000000000025,
                "bid_date": 1657753555786,
                "ask_date": 1657753555786,
                "ask": 4.020000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C125",
                "spreadPct": 0.0024906600249065456,
                "midBidAsk": 4.015000000000002
            },
            {
                "symbol": "AAPL20220713P125",
                "type": "option",
                "underlying": "AAPL",
                "strike": 125,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.030000000000002,
                "bid_date": 1657753555787,
                "ask_date": 1657753555786,
                "ask": 4.040000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.786-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P125",
                "spreadPct": 0.0024783147459726844,
                "midBidAsk": 4.035000000000002
            },
            {
                "symbol": "AAPL20220713C126",
                "type": "option",
                "underlying": "AAPL",
                "strike": 126,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.050000000000002,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.060000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C126",
                "spreadPct": 0.0024660912453760256,
                "midBidAsk": 4.0550000000000015
            },
            {
                "symbol": "AAPL20220713P126",
                "type": "option",
                "underlying": "AAPL",
                "strike": 126,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.070000000000001,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.080000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P126",
                "spreadPct": 0.002453987730061297,
                "midBidAsk": 4.075000000000001
            },
            {
                "symbol": "AAPL20220713C127",
                "type": "option",
                "underlying": "AAPL",
                "strike": 127,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.090000000000001,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.1000000000000005,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C127",
                "spreadPct": 0.0024420024420023895,
                "midBidAsk": 4.095000000000001
            },
            {
                "symbol": "AAPL20220713P127",
                "type": "option",
                "underlying": "AAPL",
                "strike": 127,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.11,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.12,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P127",
                "spreadPct": 0.0024301336573511025,
                "midBidAsk": 4.115
            },
            {
                "symbol": "AAPL20220713C128",
                "type": "option",
                "underlying": "AAPL",
                "strike": 128,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.13,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.14,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C128",
                "spreadPct": 0.0024183796856105896,
                "midBidAsk": 4.135
            },
            {
                "symbol": "AAPL20220713P128",
                "type": "option",
                "underlying": "AAPL",
                "strike": 128,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.1499999999999995,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.159999999999999,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P128",
                "spreadPct": 0.0024067388688326806,
                "midBidAsk": 4.154999999999999
            },
            {
                "symbol": "AAPL20220713C129",
                "type": "option",
                "underlying": "AAPL",
                "strike": 129,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.169999999999999,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.179999999999999,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C129",
                "spreadPct": 0.002395209580838273,
                "midBidAsk": 4.174999999999999
            },
            {
                "symbol": "AAPL20220713P129",
                "type": "option",
                "underlying": "AAPL",
                "strike": 129,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.189999999999999,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.199999999999998,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P129",
                "spreadPct": 0.0023837902264600215,
                "midBidAsk": 4.1949999999999985
            },
            {
                "symbol": "AAPL20220713C130",
                "type": "option",
                "underlying": "AAPL",
                "strike": 130,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.209999999999998,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.219999999999998,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C130",
                "spreadPct": 0.0023724792408065932,
                "midBidAsk": 4.214999999999998
            },
            {
                "symbol": "AAPL20220713P130",
                "type": "option",
                "underlying": "AAPL",
                "strike": 130,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.229999999999998,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.2399999999999975,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P130",
                "spreadPct": 0.0023612750885477667,
                "midBidAsk": 4.234999999999998
            },
            {
                "symbol": "AAPL20220713C131",
                "type": "option",
                "underlying": "AAPL",
                "strike": 131,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.249999999999997,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.259999999999997,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C131",
                "spreadPct": 0.002350176263219693,
                "midBidAsk": 4.254999999999997
            },
            {
                "symbol": "AAPL20220713P131",
                "type": "option",
                "underlying": "AAPL",
                "strike": 131,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.269999999999997,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.279999999999997,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P131",
                "spreadPct": 0.0023391812865496595,
                "midBidAsk": 4.274999999999997
            },
            {
                "symbol": "AAPL20220713C132",
                "type": "option",
                "underlying": "AAPL",
                "strike": 132,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.2899999999999965,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.299999999999996,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C132",
                "spreadPct": 0.0023282887077997194,
                "midBidAsk": 4.294999999999996
            },
            {
                "symbol": "AAPL20220713P132",
                "type": "option",
                "underlying": "AAPL",
                "strike": 132,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.309999999999996,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.319999999999996,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P132",
                "spreadPct": 0.0023174971031285738,
                "midBidAsk": 4.314999999999996
            },
            {
                "symbol": "AAPL20220713C133",
                "type": "option",
                "underlying": "AAPL",
                "strike": 133,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.329999999999996,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.339999999999995,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C133",
                "spreadPct": 0.002306805074971118,
                "midBidAsk": 4.3349999999999955
            },
            {
                "symbol": "AAPL20220713P133",
                "type": "option",
                "underlying": "AAPL",
                "strike": 133,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.349999999999995,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.359999999999995,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P133",
                "spreadPct": 0.0022962112514350857,
                "midBidAsk": 4.354999999999995
            },
            {
                "symbol": "AAPL20220713C134",
                "type": "option",
                "underlying": "AAPL",
                "strike": 134,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.369999999999995,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.379999999999995,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C134",
                "spreadPct": 0.00228571428571424,
                "midBidAsk": 4.374999999999995
            },
            {
                "symbol": "AAPL20220713P134",
                "type": "option",
                "underlying": "AAPL",
                "strike": 134,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.389999999999994,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.399999999999994,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P134",
                "spreadPct": 0.002275312855517588,
                "midBidAsk": 4.394999999999994
            },
            {
                "symbol": "AAPL20220713C135",
                "type": "option",
                "underlying": "AAPL",
                "strike": 135,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.409999999999994,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.419999999999994,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C135",
                "spreadPct": 0.0022650056625141113,
                "midBidAsk": 4.414999999999994
            },
            {
                "symbol": "AAPL20220713P135",
                "type": "option",
                "underlying": "AAPL",
                "strike": 135,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.4299999999999935,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.439999999999993,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P135",
                "spreadPct": 0.0022547914317925144,
                "midBidAsk": 4.434999999999993
            },
            {
                "symbol": "AAPL20220713C136",
                "type": "option",
                "underlying": "AAPL",
                "strike": 136,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.449999999999993,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.459999999999993,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C136",
                "spreadPct": 0.0022446689113355335,
                "midBidAsk": 4.454999999999993
            },
            {
                "symbol": "AAPL20220713P136",
                "type": "option",
                "underlying": "AAPL",
                "strike": 136,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.469999999999993,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.479999999999992,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P136",
                "spreadPct": 0.002234636871508336,
                "midBidAsk": 4.4749999999999925
            },
            {
                "symbol": "AAPL20220713C137",
                "type": "option",
                "underlying": "AAPL",
                "strike": 137,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.489999999999992,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.499999999999992,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C137",
                "spreadPct": 0.0022246941045605795,
                "midBidAsk": 4.494999999999992
            },
            {
                "symbol": "AAPL20220713P137",
                "type": "option",
                "underlying": "AAPL",
                "strike": 137,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.509999999999992,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.519999999999992,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P137",
                "spreadPct": 0.0022148394241417067,
                "midBidAsk": 4.514999999999992
            },
            {
                "symbol": "AAPL20220713C138",
                "type": "option",
                "underlying": "AAPL",
                "strike": 138,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.529999999999991,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.539999999999991,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C138",
                "spreadPct": 0.002205071664829064,
                "midBidAsk": 4.534999999999991
            },
            {
                "symbol": "AAPL20220713P138",
                "type": "option",
                "underlying": "AAPL",
                "strike": 138,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.549999999999991,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.559999999999991,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P138",
                "spreadPct": 0.0021953896816684537,
                "midBidAsk": 4.554999999999991
            },
            {
                "symbol": "AAPL20220713C139",
                "type": "option",
                "underlying": "AAPL",
                "strike": 139,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.5699999999999905,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.57999999999999,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C139",
                "spreadPct": 0.002185792349726734,
                "midBidAsk": 4.57499999999999
            },
            {
                "symbol": "AAPL20220713P139",
                "type": "option",
                "underlying": "AAPL",
                "strike": 139,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.58999999999999,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.59999999999999,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P139",
                "spreadPct": 0.0021762785636561064,
                "midBidAsk": 4.59499999999999
            },
            {
                "symbol": "AAPL20220713C140",
                "type": "option",
                "underlying": "AAPL",
                "strike": 140,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.60999999999999,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.6199999999999894,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C140",
                "spreadPct": 0.002166847237269731,
                "midBidAsk": 4.6149999999999896
            },
            {
                "symbol": "AAPL20220713P140",
                "type": "option",
                "underlying": "AAPL",
                "strike": 140,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.629999999999989,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.639999999999989,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P140",
                "spreadPct": 0.00215749730312833,
                "midBidAsk": 4.634999999999989
            },
            {
                "symbol": "AAPL20220713C141",
                "type": "option",
                "underlying": "AAPL",
                "strike": 141,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.649999999999989,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.659999999999989,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C141",
                "spreadPct": 0.002148227712137446,
                "midBidAsk": 4.654999999999989
            },
            {
                "symbol": "AAPL20220713P141",
                "type": "option",
                "underlying": "AAPL",
                "strike": 141,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.669999999999988,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.679999999999988,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P141",
                "spreadPct": 0.00213903743315504,
                "midBidAsk": 4.674999999999988
            },
            {
                "symbol": "AAPL20220713C142",
                "type": "option",
                "underlying": "AAPL",
                "strike": 142,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.689999999999988,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.699999999999988,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C142",
                "spreadPct": 0.002129925452609119,
                "midBidAsk": 4.694999999999988
            },
            {
                "symbol": "AAPL20220713P142",
                "type": "option",
                "underlying": "AAPL",
                "strike": 142,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.7099999999999875,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.719999999999987,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P142",
                "spreadPct": 0.002120890774125093,
                "midBidAsk": 4.714999999999987
            },
            {
                "symbol": "AAPL20220713C143",
                "type": "option",
                "underlying": "AAPL",
                "strike": 143,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.729999999999987,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.739999999999987,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C143",
                "spreadPct": 0.0021119324181625796,
                "midBidAsk": 4.734999999999987
            },
            {
                "symbol": "AAPL20220713P143",
                "type": "option",
                "underlying": "AAPL",
                "strike": 143,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.749999999999987,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.7599999999999865,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P143",
                "spreadPct": 0.00210304942166137,
                "midBidAsk": 4.754999999999987
            },
            {
                "symbol": "AAPL20220713C144",
                "type": "option",
                "underlying": "AAPL",
                "strike": 144,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.769999999999986,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.779999999999986,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C144",
                "spreadPct": 0.0020942408376962967,
                "midBidAsk": 4.774999999999986
            },
            {
                "symbol": "AAPL20220713P144",
                "type": "option",
                "underlying": "AAPL",
                "strike": 144,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.789999999999986,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.799999999999986,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P144",
                "spreadPct": 0.0020855057351407335,
                "midBidAsk": 4.794999999999986
            },
            {
                "symbol": "AAPL20220713C145",
                "type": "option",
                "underlying": "AAPL",
                "strike": 145,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.809999999999985,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.819999999999985,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C145",
                "spreadPct": 0.0020768431983384877,
                "midBidAsk": 4.814999999999985
            },
            {
                "symbol": "AAPL20220713P145",
                "type": "option",
                "underlying": "AAPL",
                "strike": 145,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.829999999999985,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.839999999999985,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P145",
                "spreadPct": 0.00206825232678383,
                "midBidAsk": 4.834999999999985
            },
            {
                "symbol": "AAPL20220713C146",
                "type": "option",
                "underlying": "AAPL",
                "strike": 146,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.8499999999999845,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.859999999999984,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C146",
                "spreadPct": 0.0020597322348094374,
                "midBidAsk": 4.854999999999984
            },
            {
                "symbol": "AAPL20220713P146",
                "type": "option",
                "underlying": "AAPL",
                "strike": 146,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.869999999999984,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.879999999999984,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P146",
                "spreadPct": 0.0020512820512820144,
                "midBidAsk": 4.874999999999984
            },
            {
                "symbol": "AAPL20220713C147",
                "type": "option",
                "underlying": "AAPL",
                "strike": 147,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.889999999999984,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.8999999999999835,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C147",
                "spreadPct": 0.002042900919305377,
                "midBidAsk": 4.894999999999984
            },
            {
                "symbol": "AAPL20220713P147",
                "type": "option",
                "underlying": "AAPL",
                "strike": 147,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.909999999999983,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.919999999999983,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P147",
                "spreadPct": 0.0020345879959307875,
                "midBidAsk": 4.914999999999983
            },
            {
                "symbol": "AAPL20220713C148",
                "type": "option",
                "underlying": "AAPL",
                "strike": 148,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.929999999999983,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.939999999999983,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C148",
                "spreadPct": 0.0020263424518743305,
                "midBidAsk": 4.934999999999983
            },
            {
                "symbol": "AAPL20220713P148",
                "type": "option",
                "underlying": "AAPL",
                "strike": 148,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.949999999999982,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.959999999999982,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P148",
                "spreadPct": 0.0020181634712411346,
                "midBidAsk": 4.954999999999982
            },
            {
                "symbol": "AAPL20220713C149",
                "type": "option",
                "underlying": "AAPL",
                "strike": 149,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.969999999999982,
                "bid_date": 1657753555787,
                "ask_date": 1657753555787,
                "ask": 4.979999999999982,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.787-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713C149",
                "spreadPct": 0.002010050251256246,
                "midBidAsk": 4.974999999999982
            },
            {
                "symbol": "AAPL20220713P149",
                "type": "option",
                "underlying": "AAPL",
                "strike": 149,
                "last": null,
                "expiration_date": "2022-07-13",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.989999999999982,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 4.999999999999981,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220713P149",
                "spreadPct": 0.002002002002001967,
                "midBidAsk": 4.9949999999999815
            }
        ]
    },
    {
        "2022-07-20": [
            {
                "symbol": "AAPL20220720C50",
                "type": "option",
                "underlying": "AAPL",
                "strike": 50,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.01,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.02,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C50",
                "spreadPct": 0.00985221674876848,
                "midBidAsk": 1.0150000000000001
            },
            {
                "symbol": "AAPL20220720P50",
                "type": "option",
                "underlying": "AAPL",
                "strike": 50,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.03,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.04,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P50",
                "spreadPct": 0.009661835748792277,
                "midBidAsk": 1.0350000000000001
            },
            {
                "symbol": "AAPL20220720C51",
                "type": "option",
                "underlying": "AAPL",
                "strike": 51,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.05,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.06,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C51",
                "spreadPct": 0.009478672985781998,
                "midBidAsk": 1.0550000000000002
            },
            {
                "symbol": "AAPL20220720P51",
                "type": "option",
                "underlying": "AAPL",
                "strike": 51,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.07,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.08,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P51",
                "spreadPct": 0.009302325581395356,
                "midBidAsk": 1.0750000000000002
            },
            {
                "symbol": "AAPL20220720C52",
                "type": "option",
                "underlying": "AAPL",
                "strike": 52,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.09,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.1,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C52",
                "spreadPct": 0.009132420091324207,
                "midBidAsk": 1.0950000000000002
            },
            {
                "symbol": "AAPL20220720P52",
                "type": "option",
                "underlying": "AAPL",
                "strike": 52,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.11,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.12,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P52",
                "spreadPct": 0.008968609865470859,
                "midBidAsk": 1.1150000000000002
            },
            {
                "symbol": "AAPL20220720C53",
                "type": "option",
                "underlying": "AAPL",
                "strike": 53,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.1300000000000001,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.1400000000000001,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C53",
                "spreadPct": 0.008810572687224676,
                "midBidAsk": 1.1350000000000002
            },
            {
                "symbol": "AAPL20220720P53",
                "type": "option",
                "underlying": "AAPL",
                "strike": 53,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.1500000000000001,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.1600000000000001,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P53",
                "spreadPct": 0.008658008658008663,
                "midBidAsk": 1.1550000000000002
            },
            {
                "symbol": "AAPL20220720C54",
                "type": "option",
                "underlying": "AAPL",
                "strike": 54,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.1700000000000002,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.1800000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C54",
                "spreadPct": 0.008510638297872346,
                "midBidAsk": 1.1750000000000003
            },
            {
                "symbol": "AAPL20220720P54",
                "type": "option",
                "underlying": "AAPL",
                "strike": 54,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.1900000000000002,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.2000000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P54",
                "spreadPct": 0.00836820083682009,
                "midBidAsk": 1.1950000000000003
            },
            {
                "symbol": "AAPL20220720C55",
                "type": "option",
                "underlying": "AAPL",
                "strike": 55,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.2100000000000002,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.2200000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C55",
                "spreadPct": 0.008230452674897125,
                "midBidAsk": 1.2150000000000003
            },
            {
                "symbol": "AAPL20220720P55",
                "type": "option",
                "underlying": "AAPL",
                "strike": 55,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.2300000000000002,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.2400000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P55",
                "spreadPct": 0.00809716599190284,
                "midBidAsk": 1.2350000000000003
            },
            {
                "symbol": "AAPL20220720C56",
                "type": "option",
                "underlying": "AAPL",
                "strike": 56,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.2500000000000002,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.2600000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C56",
                "spreadPct": 0.007968127490039846,
                "midBidAsk": 1.2550000000000003
            },
            {
                "symbol": "AAPL20220720P56",
                "type": "option",
                "underlying": "AAPL",
                "strike": 56,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.2700000000000002,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.2800000000000002,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P56",
                "spreadPct": 0.007843137254901966,
                "midBidAsk": 1.2750000000000004
            },
            {
                "symbol": "AAPL20220720C57",
                "type": "option",
                "underlying": "AAPL",
                "strike": 57,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.2900000000000003,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.3000000000000003,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C57",
                "spreadPct": 0.0077220077220077265,
                "midBidAsk": 1.2950000000000004
            },
            {
                "symbol": "AAPL20220720P57",
                "type": "option",
                "underlying": "AAPL",
                "strike": 57,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.3100000000000003,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.3200000000000003,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P57",
                "spreadPct": 0.00760456273764259,
                "midBidAsk": 1.3150000000000004
            },
            {
                "symbol": "AAPL20220720C58",
                "type": "option",
                "underlying": "AAPL",
                "strike": 58,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.3300000000000003,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.3400000000000003,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C58",
                "spreadPct": 0.007490636704119855,
                "midBidAsk": 1.3350000000000004
            },
            {
                "symbol": "AAPL20220720P58",
                "type": "option",
                "underlying": "AAPL",
                "strike": 58,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.3500000000000003,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.3600000000000003,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P58",
                "spreadPct": 0.0073800738007380115,
                "midBidAsk": 1.3550000000000004
            },
            {
                "symbol": "AAPL20220720C59",
                "type": "option",
                "underlying": "AAPL",
                "strike": 59,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.3700000000000003,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.3800000000000003,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C59",
                "spreadPct": 0.007272727272727277,
                "midBidAsk": 1.3750000000000004
            },
            {
                "symbol": "AAPL20220720P59",
                "type": "option",
                "underlying": "AAPL",
                "strike": 59,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.3900000000000003,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.4000000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P59",
                "spreadPct": 0.0071684587813620115,
                "midBidAsk": 1.3950000000000005
            },
            {
                "symbol": "AAPL20220720C60",
                "type": "option",
                "underlying": "AAPL",
                "strike": 60,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.4100000000000004,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.4200000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C60",
                "spreadPct": 0.007067137809187283,
                "midBidAsk": 1.4150000000000005
            },
            {
                "symbol": "AAPL20220720P60",
                "type": "option",
                "underlying": "AAPL",
                "strike": 60,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.4300000000000004,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.4400000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P60",
                "spreadPct": 0.006968641114982582,
                "midBidAsk": 1.4350000000000005
            },
            {
                "symbol": "AAPL20220720C61",
                "type": "option",
                "underlying": "AAPL",
                "strike": 61,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.4500000000000004,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.4600000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C61",
                "spreadPct": 0.006872852233676979,
                "midBidAsk": 1.4550000000000005
            },
            {
                "symbol": "AAPL20220720P61",
                "type": "option",
                "underlying": "AAPL",
                "strike": 61,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.4700000000000004,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.4800000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P61",
                "spreadPct": 0.006779661016949156,
                "midBidAsk": 1.4750000000000005
            },
            {
                "symbol": "AAPL20220720C62",
                "type": "option",
                "underlying": "AAPL",
                "strike": 62,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.4900000000000004,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.5000000000000004,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C62",
                "spreadPct": 0.0066889632107023445,
                "midBidAsk": 1.4950000000000006
            },
            {
                "symbol": "AAPL20220720P62",
                "type": "option",
                "underlying": "AAPL",
                "strike": 62,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.5100000000000005,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.5200000000000005,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P62",
                "spreadPct": 0.006600660066006604,
                "midBidAsk": 1.5150000000000006
            },
            {
                "symbol": "AAPL20220720C63",
                "type": "option",
                "underlying": "AAPL",
                "strike": 63,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.5300000000000005,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.5400000000000005,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C63",
                "spreadPct": 0.006514657980456029,
                "midBidAsk": 1.5350000000000006
            },
            {
                "symbol": "AAPL20220720P63",
                "type": "option",
                "underlying": "AAPL",
                "strike": 63,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.5500000000000005,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.5600000000000005,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P63",
                "spreadPct": 0.006430868167202576,
                "midBidAsk": 1.5550000000000006
            },
            {
                "symbol": "AAPL20220720C64",
                "type": "option",
                "underlying": "AAPL",
                "strike": 64,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.5700000000000005,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.5800000000000005,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C64",
                "spreadPct": 0.006349206349206353,
                "midBidAsk": 1.5750000000000006
            },
            {
                "symbol": "AAPL20220720P64",
                "type": "option",
                "underlying": "AAPL",
                "strike": 64,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.5900000000000005,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.6000000000000005,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P64",
                "spreadPct": 0.0062695924764890314,
                "midBidAsk": 1.5950000000000006
            },
            {
                "symbol": "AAPL20220720C65",
                "type": "option",
                "underlying": "AAPL",
                "strike": 65,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.6100000000000005,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.6200000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C65",
                "spreadPct": 0.006191950464396288,
                "midBidAsk": 1.6150000000000007
            },
            {
                "symbol": "AAPL20220720P65",
                "type": "option",
                "underlying": "AAPL",
                "strike": 65,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.6300000000000006,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.6400000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P65",
                "spreadPct": 0.006116207951070339,
                "midBidAsk": 1.6350000000000007
            },
            {
                "symbol": "AAPL20220720C66",
                "type": "option",
                "underlying": "AAPL",
                "strike": 66,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.6500000000000006,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.6600000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C66",
                "spreadPct": 0.0060422960725075555,
                "midBidAsk": 1.6550000000000007
            },
            {
                "symbol": "AAPL20220720P66",
                "type": "option",
                "underlying": "AAPL",
                "strike": 66,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.6700000000000006,
                "bid_date": 1657753555788,
                "ask_date": 1657753555788,
                "ask": 1.6800000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.788-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P66",
                "spreadPct": 0.005970149253731346,
                "midBidAsk": 1.6750000000000007
            },
            {
                "symbol": "AAPL20220720C67",
                "type": "option",
                "underlying": "AAPL",
                "strike": 67,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.6900000000000006,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.7000000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C67",
                "spreadPct": 0.005899705014749265,
                "midBidAsk": 1.6950000000000007
            },
            {
                "symbol": "AAPL20220720P67",
                "type": "option",
                "underlying": "AAPL",
                "strike": 67,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.7100000000000006,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.7200000000000006,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P67",
                "spreadPct": 0.005830903790087466,
                "midBidAsk": 1.7150000000000007
            },
            {
                "symbol": "AAPL20220720C68",
                "type": "option",
                "underlying": "AAPL",
                "strike": 68,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.7300000000000006,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.7400000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C68",
                "spreadPct": 0.005763688760806919,
                "midBidAsk": 1.7350000000000008
            },
            {
                "symbol": "AAPL20220720P68",
                "type": "option",
                "underlying": "AAPL",
                "strike": 68,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.7500000000000007,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.7600000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P68",
                "spreadPct": 0.005698005698005701,
                "midBidAsk": 1.7550000000000008
            },
            {
                "symbol": "AAPL20220720C69",
                "type": "option",
                "underlying": "AAPL",
                "strike": 69,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.7700000000000007,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.7800000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C69",
                "spreadPct": 0.0056338028169014105,
                "midBidAsk": 1.7750000000000008
            },
            {
                "symbol": "AAPL20220720P69",
                "type": "option",
                "underlying": "AAPL",
                "strike": 69,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.7900000000000007,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.8000000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P69",
                "spreadPct": 0.005571030640668526,
                "midBidAsk": 1.7950000000000008
            },
            {
                "symbol": "AAPL20220720C70",
                "type": "option",
                "underlying": "AAPL",
                "strike": 70,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.8100000000000007,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.8200000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C70",
                "spreadPct": 0.00550964187327824,
                "midBidAsk": 1.8150000000000008
            },
            {
                "symbol": "AAPL20220720P70",
                "type": "option",
                "underlying": "AAPL",
                "strike": 70,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.8300000000000007,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.8400000000000007,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P70",
                "spreadPct": 0.005449591280653953,
                "midBidAsk": 1.8350000000000009
            },
            {
                "symbol": "AAPL20220720C71",
                "type": "option",
                "underlying": "AAPL",
                "strike": 71,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.8500000000000008,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.8600000000000008,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C71",
                "spreadPct": 0.005390835579514827,
                "midBidAsk": 1.8550000000000009
            },
            {
                "symbol": "AAPL20220720P71",
                "type": "option",
                "underlying": "AAPL",
                "strike": 71,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.8700000000000008,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.8800000000000008,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P71",
                "spreadPct": 0.005333333333333336,
                "midBidAsk": 1.8750000000000009
            },
            {
                "symbol": "AAPL20220720C72",
                "type": "option",
                "underlying": "AAPL",
                "strike": 72,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.8900000000000008,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.9000000000000008,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C72",
                "spreadPct": 0.005277044854881269,
                "midBidAsk": 1.895000000000001
            },
            {
                "symbol": "AAPL20220720P72",
                "type": "option",
                "underlying": "AAPL",
                "strike": 72,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.9100000000000008,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.9200000000000008,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P72",
                "spreadPct": 0.005221932114882508,
                "midBidAsk": 1.915000000000001
            },
            {
                "symbol": "AAPL20220720C73",
                "type": "option",
                "underlying": "AAPL",
                "strike": 73,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.9300000000000008,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.9400000000000008,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C73",
                "spreadPct": 0.005167958656330751,
                "midBidAsk": 1.935000000000001
            },
            {
                "symbol": "AAPL20220720P73",
                "type": "option",
                "underlying": "AAPL",
                "strike": 73,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.9500000000000008,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.9600000000000009,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P73",
                "spreadPct": 0.005115089514066498,
                "midBidAsk": 1.955000000000001
            },
            {
                "symbol": "AAPL20220720C74",
                "type": "option",
                "underlying": "AAPL",
                "strike": 74,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.9700000000000009,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 1.9800000000000009,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C74",
                "spreadPct": 0.005063291139240508,
                "midBidAsk": 1.975000000000001
            },
            {
                "symbol": "AAPL20220720P74",
                "type": "option",
                "underlying": "AAPL",
                "strike": 74,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 1.9900000000000009,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.000000000000001,
                "spreadCents": 0.010000000000000009,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P74",
                "spreadPct": 0.005012531328320804,
                "midBidAsk": 1.995000000000001
            },
            {
                "symbol": "AAPL20220720C75",
                "type": "option",
                "underlying": "AAPL",
                "strike": 75,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.010000000000001,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.020000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C75",
                "spreadPct": 0.004962779156327435,
                "midBidAsk": 2.015000000000001
            },
            {
                "symbol": "AAPL20220720P75",
                "type": "option",
                "underlying": "AAPL",
                "strike": 75,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.030000000000001,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.040000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P75",
                "spreadPct": 0.004914004914004807,
                "midBidAsk": 2.035000000000001
            },
            {
                "symbol": "AAPL20220720C76",
                "type": "option",
                "underlying": "AAPL",
                "strike": 76,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.050000000000001,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.060000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C76",
                "spreadPct": 0.004866180048661694,
                "midBidAsk": 2.055000000000001
            },
            {
                "symbol": "AAPL20220720P76",
                "type": "option",
                "underlying": "AAPL",
                "strike": 76,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.070000000000001,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.080000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P76",
                "spreadPct": 0.0048192771084336295,
                "midBidAsk": 2.075000000000001
            },
            {
                "symbol": "AAPL20220720C77",
                "type": "option",
                "underlying": "AAPL",
                "strike": 77,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.090000000000001,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.100000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C77",
                "spreadPct": 0.004773269689737366,
                "midBidAsk": 2.095000000000001
            },
            {
                "symbol": "AAPL20220720P77",
                "type": "option",
                "underlying": "AAPL",
                "strike": 77,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.110000000000001,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.120000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P77",
                "spreadPct": 0.0047281323877067525,
                "midBidAsk": 2.115000000000001
            },
            {
                "symbol": "AAPL20220720C78",
                "type": "option",
                "underlying": "AAPL",
                "strike": 78,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.1300000000000012,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.140000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C78",
                "spreadPct": 0.0046838407494144176,
                "midBidAsk": 2.135000000000001
            },
            {
                "symbol": "AAPL20220720P78",
                "type": "option",
                "underlying": "AAPL",
                "strike": 78,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.1500000000000012,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.160000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P78",
                "spreadPct": 0.004640371229698274,
                "midBidAsk": 2.155000000000001
            },
            {
                "symbol": "AAPL20220720C79",
                "type": "option",
                "underlying": "AAPL",
                "strike": 79,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.1700000000000013,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.180000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C79",
                "spreadPct": 0.004597701149425187,
                "midBidAsk": 2.175000000000001
            },
            {
                "symbol": "AAPL20220720P79",
                "type": "option",
                "underlying": "AAPL",
                "strike": 79,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.1900000000000013,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.200000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P79",
                "spreadPct": 0.0045558086560363465,
                "midBidAsk": 2.195000000000001
            },
            {
                "symbol": "AAPL20220720C80",
                "type": "option",
                "underlying": "AAPL",
                "strike": 80,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.2100000000000013,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.220000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C80",
                "spreadPct": 0.00451467268623015,
                "midBidAsk": 2.215000000000001
            },
            {
                "symbol": "AAPL20220720P80",
                "type": "option",
                "underlying": "AAPL",
                "strike": 80,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.2300000000000013,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.240000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P80",
                "spreadPct": 0.0044742729306486715,
                "midBidAsk": 2.235000000000001
            },
            {
                "symbol": "AAPL20220720C81",
                "type": "option",
                "underlying": "AAPL",
                "strike": 81,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.2500000000000013,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.260000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C81",
                "spreadPct": 0.004434589800443362,
                "midBidAsk": 2.2550000000000012
            },
            {
                "symbol": "AAPL20220720P81",
                "type": "option",
                "underlying": "AAPL",
                "strike": 81,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.2700000000000014,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.280000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P81",
                "spreadPct": 0.004395604395604299,
                "midBidAsk": 2.2750000000000012
            },
            {
                "symbol": "AAPL20220720C82",
                "type": "option",
                "underlying": "AAPL",
                "strike": 82,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.2900000000000014,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.300000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C82",
                "spreadPct": 0.004357298474945439,
                "midBidAsk": 2.2950000000000013
            },
            {
                "symbol": "AAPL20220720P82",
                "type": "option",
                "underlying": "AAPL",
                "strike": 82,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.3100000000000014,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.320000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P82",
                "spreadPct": 0.004319654427645694,
                "midBidAsk": 2.3150000000000013
            },
            {
                "symbol": "AAPL20220720C83",
                "type": "option",
                "underlying": "AAPL",
                "strike": 83,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.3300000000000014,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.340000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C83",
                "spreadPct": 0.004282655246252583,
                "midBidAsk": 2.3350000000000013
            },
            {
                "symbol": "AAPL20220720P83",
                "type": "option",
                "underlying": "AAPL",
                "strike": 83,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.3500000000000014,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.360000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P83",
                "spreadPct": 0.004246284501061478,
                "midBidAsk": 2.3550000000000013
            },
            {
                "symbol": "AAPL20220720C84",
                "type": "option",
                "underlying": "AAPL",
                "strike": 84,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.3700000000000014,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.3800000000000012,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C84",
                "spreadPct": 0.004210526315789382,
                "midBidAsk": 2.3750000000000013
            },
            {
                "symbol": "AAPL20220720P84",
                "type": "option",
                "underlying": "AAPL",
                "strike": 84,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.3900000000000015,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.4000000000000012,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P84",
                "spreadPct": 0.004175365344467549,
                "midBidAsk": 2.3950000000000014
            },
            {
                "symbol": "AAPL20220720C85",
                "type": "option",
                "underlying": "AAPL",
                "strike": 85,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.4100000000000015,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.4200000000000013,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C85",
                "spreadPct": 0.004140786749482311,
                "midBidAsk": 2.4150000000000014
            },
            {
                "symbol": "AAPL20220720P85",
                "type": "option",
                "underlying": "AAPL",
                "strike": 85,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.4300000000000015,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.4400000000000013,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P85",
                "spreadPct": 0.004106776180698062,
                "midBidAsk": 2.4350000000000014
            },
            {
                "symbol": "AAPL20220720C86",
                "type": "option",
                "underlying": "AAPL",
                "strike": 86,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.4500000000000015,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.4600000000000013,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C86",
                "spreadPct": 0.004073319755600726,
                "midBidAsk": 2.4550000000000014
            },
            {
                "symbol": "AAPL20220720P86",
                "type": "option",
                "underlying": "AAPL",
                "strike": 86,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.4700000000000015,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.4800000000000013,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P86",
                "spreadPct": 0.004040404040403952,
                "midBidAsk": 2.4750000000000014
            },
            {
                "symbol": "AAPL20220720C87",
                "type": "option",
                "underlying": "AAPL",
                "strike": 87,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.4900000000000015,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.5000000000000013,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C87",
                "spreadPct": 0.00400801603206404,
                "midBidAsk": 2.4950000000000014
            },
            {
                "symbol": "AAPL20220720P87",
                "type": "option",
                "underlying": "AAPL",
                "strike": 87,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.5100000000000016,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.5200000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P87",
                "spreadPct": 0.003976143141152994,
                "midBidAsk": 2.5150000000000015
            },
            {
                "symbol": "AAPL20220720C88",
                "type": "option",
                "underlying": "AAPL",
                "strike": 88,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.5300000000000016,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.5400000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C88",
                "spreadPct": 0.00394477317554232,
                "midBidAsk": 2.5350000000000015
            },
            {
                "symbol": "AAPL20220720P88",
                "type": "option",
                "underlying": "AAPL",
                "strike": 88,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.5500000000000016,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.5600000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P88",
                "spreadPct": 0.003913894324853143,
                "midBidAsk": 2.5550000000000015
            },
            {
                "symbol": "AAPL20220720C89",
                "type": "option",
                "underlying": "AAPL",
                "strike": 89,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.5700000000000016,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.5800000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C89",
                "spreadPct": 0.003883495145630983,
                "midBidAsk": 2.5750000000000015
            },
            {
                "symbol": "AAPL20220720P89",
                "type": "option",
                "underlying": "AAPL",
                "strike": 89,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.5900000000000016,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.6000000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P89",
                "spreadPct": 0.003853564547206081,
                "midBidAsk": 2.5950000000000015
            },
            {
                "symbol": "AAPL20220720C90",
                "type": "option",
                "underlying": "AAPL",
                "strike": 90,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.6100000000000017,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.6200000000000014,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C90",
                "spreadPct": 0.003824091778202593,
                "midBidAsk": 2.6150000000000015
            },
            {
                "symbol": "AAPL20220720P90",
                "type": "option",
                "underlying": "AAPL",
                "strike": 90,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.6300000000000017,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.6400000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P90",
                "spreadPct": 0.003795066413662156,
                "midBidAsk": 2.6350000000000016
            },
            {
                "symbol": "AAPL20220720C91",
                "type": "option",
                "underlying": "AAPL",
                "strike": 91,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.6500000000000017,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.6600000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C91",
                "spreadPct": 0.0037664783427494466,
                "midBidAsk": 2.6550000000000016
            },
            {
                "symbol": "AAPL20220720P91",
                "type": "option",
                "underlying": "AAPL",
                "strike": 91,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.6700000000000017,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.6800000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P91",
                "spreadPct": 0.003738317757009264,
                "midBidAsk": 2.6750000000000016
            },
            {
                "symbol": "AAPL20220720C92",
                "type": "option",
                "underlying": "AAPL",
                "strike": 92,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.6900000000000017,
                "bid_date": 1657753555789,
                "ask_date": 1657753555789,
                "ask": 2.7000000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C92",
                "spreadPct": 0.0037105751391464862,
                "midBidAsk": 2.6950000000000016
            },
            {
                "symbol": "AAPL20220720P92",
                "type": "option",
                "underlying": "AAPL",
                "strike": 92,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.7100000000000017,
                "bid_date": 1657753555790,
                "ask_date": 1657753555789,
                "ask": 2.7200000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.789-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P92",
                "spreadPct": 0.0036832412523019453,
                "midBidAsk": 2.7150000000000016
            },
            {
                "symbol": "AAPL20220720C93",
                "type": "option",
                "underlying": "AAPL",
                "strike": 93,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.7300000000000018,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.7400000000000015,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C93",
                "spreadPct": 0.003656307129798823,
                "midBidAsk": 2.7350000000000017
            },
            {
                "symbol": "AAPL20220720P93",
                "type": "option",
                "underlying": "AAPL",
                "strike": 93,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.7500000000000018,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.7600000000000016,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P93",
                "spreadPct": 0.0036297640653356737,
                "midBidAsk": 2.7550000000000017
            },
            {
                "symbol": "AAPL20220720C94",
                "type": "option",
                "underlying": "AAPL",
                "strike": 94,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.770000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.7800000000000016,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C94",
                "spreadPct": 0.0036036036036035247,
                "midBidAsk": 2.7750000000000017
            },
            {
                "symbol": "AAPL20220720P94",
                "type": "option",
                "underlying": "AAPL",
                "strike": 94,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.790000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.8000000000000016,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P94",
                "spreadPct": 0.003577817531305825,
                "midBidAsk": 2.7950000000000017
            },
            {
                "symbol": "AAPL20220720C95",
                "type": "option",
                "underlying": "AAPL",
                "strike": 95,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.810000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.8200000000000016,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C95",
                "spreadPct": 0.003552397868561201,
                "midBidAsk": 2.8150000000000017
            },
            {
                "symbol": "AAPL20220720P95",
                "type": "option",
                "underlying": "AAPL",
                "strike": 95,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.830000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.8400000000000016,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P95",
                "spreadPct": 0.0035273368606701166,
                "midBidAsk": 2.8350000000000017
            },
            {
                "symbol": "AAPL20220720C96",
                "type": "option",
                "underlying": "AAPL",
                "strike": 96,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.850000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.8600000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C96",
                "spreadPct": 0.003502626970227594,
                "midBidAsk": 2.8550000000000018
            },
            {
                "symbol": "AAPL20220720P96",
                "type": "option",
                "underlying": "AAPL",
                "strike": 96,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.870000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.8800000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P96",
                "spreadPct": 0.003478260869565141,
                "midBidAsk": 2.8750000000000018
            },
            {
                "symbol": "AAPL20220720C97",
                "type": "option",
                "underlying": "AAPL",
                "strike": 97,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.890000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.9000000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C97",
                "spreadPct": 0.0034542314335059693,
                "midBidAsk": 2.895000000000002
            },
            {
                "symbol": "AAPL20220720P97",
                "type": "option",
                "underlying": "AAPL",
                "strike": 97,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.910000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.9200000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P97",
                "spreadPct": 0.00343053173241845,
                "midBidAsk": 2.915000000000002
            },
            {
                "symbol": "AAPL20220720C98",
                "type": "option",
                "underlying": "AAPL",
                "strike": 98,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.930000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.9400000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C98",
                "spreadPct": 0.003407155025553588,
                "midBidAsk": 2.935000000000002
            },
            {
                "symbol": "AAPL20220720P98",
                "type": "option",
                "underlying": "AAPL",
                "strike": 98,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.950000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.9600000000000017,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P98",
                "spreadPct": 0.003384094754653056,
                "midBidAsk": 2.955000000000002
            },
            {
                "symbol": "AAPL20220720C99",
                "type": "option",
                "underlying": "AAPL",
                "strike": 99,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.970000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 2.9800000000000018,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C99",
                "spreadPct": 0.003361344537815052,
                "midBidAsk": 2.975000000000002
            },
            {
                "symbol": "AAPL20220720P99",
                "type": "option",
                "underlying": "AAPL",
                "strike": 99,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 2.990000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.0000000000000018,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P99",
                "spreadPct": 0.003338898163605937,
                "midBidAsk": 2.995000000000002
            },
            {
                "symbol": "AAPL20220720C100",
                "type": "option",
                "underlying": "AAPL",
                "strike": 100,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.010000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.020000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C100",
                "spreadPct": 0.003316749585406229,
                "midBidAsk": 3.015000000000002
            },
            {
                "symbol": "AAPL20220720P100",
                "type": "option",
                "underlying": "AAPL",
                "strike": 100,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.030000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.040000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P100",
                "spreadPct": 0.0032948929159801583,
                "midBidAsk": 3.035000000000002
            },
            {
                "symbol": "AAPL20220720C101",
                "type": "option",
                "underlying": "AAPL",
                "strike": 101,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.050000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.060000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C101",
                "spreadPct": 0.0032733224222585206,
                "midBidAsk": 3.055000000000002
            },
            {
                "symbol": "AAPL20220720P101",
                "type": "option",
                "underlying": "AAPL",
                "strike": 101,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.070000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.080000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P101",
                "spreadPct": 0.0032520325203251317,
                "midBidAsk": 3.075000000000002
            },
            {
                "symbol": "AAPL20220720C102",
                "type": "option",
                "underlying": "AAPL",
                "strike": 102,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.090000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.100000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C102",
                "spreadPct": 0.0032310177705976674,
                "midBidAsk": 3.095000000000002
            },
            {
                "symbol": "AAPL20220720P102",
                "type": "option",
                "underlying": "AAPL",
                "strike": 102,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.110000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.120000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P102",
                "spreadPct": 0.0032102728731941512,
                "midBidAsk": 3.115000000000002
            },
            {
                "symbol": "AAPL20220720C103",
                "type": "option",
                "underlying": "AAPL",
                "strike": 103,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.130000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.140000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C103",
                "spreadPct": 0.0031897926634768038,
                "midBidAsk": 3.135000000000002
            },
            {
                "symbol": "AAPL20220720P103",
                "type": "option",
                "underlying": "AAPL",
                "strike": 103,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.150000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.160000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P103",
                "spreadPct": 0.003169572107765382,
                "midBidAsk": 3.155000000000002
            },
            {
                "symbol": "AAPL20220720C104",
                "type": "option",
                "underlying": "AAPL",
                "strike": 104,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.170000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.180000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C104",
                "spreadPct": 0.0031496062992125294,
                "midBidAsk": 3.175000000000002
            },
            {
                "symbol": "AAPL20220720P104",
                "type": "option",
                "underlying": "AAPL",
                "strike": 104,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.190000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.200000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P104",
                "spreadPct": 0.003129890453834047,
                "midBidAsk": 3.195000000000002
            },
            {
                "symbol": "AAPL20220720C105",
                "type": "option",
                "underlying": "AAPL",
                "strike": 105,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.210000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.220000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C105",
                "spreadPct": 0.0031104199066873343,
                "midBidAsk": 3.215000000000002
            },
            {
                "symbol": "AAPL20220720P105",
                "type": "option",
                "underlying": "AAPL",
                "strike": 105,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.230000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.240000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P105",
                "spreadPct": 0.003091190108191586,
                "midBidAsk": 3.235000000000002
            },
            {
                "symbol": "AAPL20220720C106",
                "type": "option",
                "underlying": "AAPL",
                "strike": 106,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.250000000000002,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.260000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C106",
                "spreadPct": 0.0030721966205836497,
                "midBidAsk": 3.255000000000002
            },
            {
                "symbol": "AAPL20220720P106",
                "type": "option",
                "underlying": "AAPL",
                "strike": 106,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.2700000000000022,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.280000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P106",
                "spreadPct": 0.0030534351145037495,
                "midBidAsk": 3.275000000000002
            },
            {
                "symbol": "AAPL20220720C107",
                "type": "option",
                "underlying": "AAPL",
                "strike": 107,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.2900000000000023,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.300000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C107",
                "spreadPct": 0.003034901365705548,
                "midBidAsk": 3.295000000000002
            },
            {
                "symbol": "AAPL20220720P107",
                "type": "option",
                "underlying": "AAPL",
                "strike": 107,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.3100000000000023,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.320000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P107",
                "spreadPct": 0.0030165912518853033,
                "midBidAsk": 3.315000000000002
            },
            {
                "symbol": "AAPL20220720C108",
                "type": "option",
                "underlying": "AAPL",
                "strike": 108,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.3300000000000023,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.340000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C108",
                "spreadPct": 0.0029985007496251214,
                "midBidAsk": 3.335000000000002
            },
            {
                "symbol": "AAPL20220720P108",
                "type": "option",
                "underlying": "AAPL",
                "strike": 108,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.3500000000000023,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.360000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P108",
                "spreadPct": 0.002980625931445538,
                "midBidAsk": 3.355000000000002
            },
            {
                "symbol": "AAPL20220720C109",
                "type": "option",
                "underlying": "AAPL",
                "strike": 109,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.3700000000000023,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.380000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C109",
                "spreadPct": 0.0029629629629628978,
                "midBidAsk": 3.375000000000002
            },
            {
                "symbol": "AAPL20220720P109",
                "type": "option",
                "underlying": "AAPL",
                "strike": 109,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.3900000000000023,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.400000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P109",
                "spreadPct": 0.0029455081001472107,
                "midBidAsk": 3.3950000000000022
            },
            {
                "symbol": "AAPL20220720C110",
                "type": "option",
                "underlying": "AAPL",
                "strike": 110,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.4100000000000024,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.420000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C110",
                "spreadPct": 0.002928257686676363,
                "midBidAsk": 3.4150000000000023
            },
            {
                "symbol": "AAPL20220720P110",
                "type": "option",
                "underlying": "AAPL",
                "strike": 110,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.4300000000000024,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.440000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P110",
                "spreadPct": 0.00291120815138276,
                "midBidAsk": 3.4350000000000023
            },
            {
                "symbol": "AAPL20220720C111",
                "type": "option",
                "underlying": "AAPL",
                "strike": 111,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.4500000000000024,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.460000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C111",
                "spreadPct": 0.0028943560057886485,
                "midBidAsk": 3.4550000000000023
            },
            {
                "symbol": "AAPL20220720P111",
                "type": "option",
                "underlying": "AAPL",
                "strike": 111,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.4700000000000024,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.480000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P111",
                "spreadPct": 0.0028776978417265555,
                "midBidAsk": 3.4750000000000023
            },
            {
                "symbol": "AAPL20220720C112",
                "type": "option",
                "underlying": "AAPL",
                "strike": 112,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.4900000000000024,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.500000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C112",
                "spreadPct": 0.002861230329041425,
                "midBidAsk": 3.4950000000000023
            },
            {
                "symbol": "AAPL20220720P112",
                "type": "option",
                "underlying": "AAPL",
                "strike": 112,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.5100000000000025,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.5200000000000022,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P112",
                "spreadPct": 0.0028449502133712033,
                "midBidAsk": 3.5150000000000023
            },
            {
                "symbol": "AAPL20220720C113",
                "type": "option",
                "underlying": "AAPL",
                "strike": 113,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.5300000000000025,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.5400000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C113",
                "spreadPct": 0.0028288543140027665,
                "midBidAsk": 3.5350000000000024
            },
            {
                "symbol": "AAPL20220720P113",
                "type": "option",
                "underlying": "AAPL",
                "strike": 113,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.5500000000000025,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.5600000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P113",
                "spreadPct": 0.0028129395218002193,
                "midBidAsk": 3.5550000000000024
            },
            {
                "symbol": "AAPL20220720C114",
                "type": "option",
                "underlying": "AAPL",
                "strike": 114,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.5700000000000025,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.5800000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C114",
                "spreadPct": 0.0027972027972027356,
                "midBidAsk": 3.5750000000000024
            },
            {
                "symbol": "AAPL20220720P114",
                "type": "option",
                "underlying": "AAPL",
                "strike": 114,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.5900000000000025,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.6000000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P114",
                "spreadPct": 0.0027816411682892294,
                "midBidAsk": 3.5950000000000024
            },
            {
                "symbol": "AAPL20220720C115",
                "type": "option",
                "underlying": "AAPL",
                "strike": 115,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.6100000000000025,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.6200000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C115",
                "spreadPct": 0.0027662517289072696,
                "midBidAsk": 3.6150000000000024
            },
            {
                "symbol": "AAPL20220720P115",
                "type": "option",
                "underlying": "AAPL",
                "strike": 115,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.6300000000000026,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.6400000000000023,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P115",
                "spreadPct": 0.0027510316368637633,
                "midBidAsk": 3.6350000000000025
            },
            {
                "symbol": "AAPL20220720C116",
                "type": "option",
                "underlying": "AAPL",
                "strike": 116,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.6500000000000026,
                "bid_date": 1657753555790,
                "ask_date": 1657753555790,
                "ask": 3.6600000000000024,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C116",
                "spreadPct": 0.0027359781121750423,
                "midBidAsk": 3.6550000000000025
            },
            {
                "symbol": "AAPL20220720P116",
                "type": "option",
                "underlying": "AAPL",
                "strike": 116,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.6700000000000026,
                "bid_date": 1657753555791,
                "ask_date": 1657753555790,
                "ask": 3.6800000000000024,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.790-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P116",
                "spreadPct": 0.0027210884353740896,
                "midBidAsk": 3.6750000000000025
            },
            {
                "symbol": "AAPL20220720C117",
                "type": "option",
                "underlying": "AAPL",
                "strike": 117,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.6900000000000026,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.7000000000000024,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C117",
                "spreadPct": 0.0027063599458727414,
                "midBidAsk": 3.6950000000000025
            },
            {
                "symbol": "AAPL20220720P117",
                "type": "option",
                "underlying": "AAPL",
                "strike": 117,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.7100000000000026,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.7200000000000024,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P117",
                "spreadPct": 0.002691790040376791,
                "midBidAsk": 3.7150000000000025
            },
            {
                "symbol": "AAPL20220720C118",
                "type": "option",
                "underlying": "AAPL",
                "strike": 118,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.7300000000000026,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.7400000000000024,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C118",
                "spreadPct": 0.002677376171352016,
                "midBidAsk": 3.7350000000000025
            },
            {
                "symbol": "AAPL20220720P118",
                "type": "option",
                "underlying": "AAPL",
                "strike": 118,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.7500000000000027,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.7600000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P118",
                "spreadPct": 0.0026631158455392226,
                "midBidAsk": 3.7550000000000026
            },
            {
                "symbol": "AAPL20220720C119",
                "type": "option",
                "underlying": "AAPL",
                "strike": 119,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.7700000000000027,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.7800000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C119",
                "spreadPct": 0.002649006622516498,
                "midBidAsk": 3.7750000000000026
            },
            {
                "symbol": "AAPL20220720P119",
                "type": "option",
                "underlying": "AAPL",
                "strike": 119,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.7900000000000027,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.8000000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P119",
                "spreadPct": 0.002635046113306925,
                "midBidAsk": 3.7950000000000026
            },
            {
                "symbol": "AAPL20220720C120",
                "type": "option",
                "underlying": "AAPL",
                "strike": 120,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.8100000000000027,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.8200000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C120",
                "spreadPct": 0.0026212319790300865,
                "midBidAsk": 3.8150000000000026
            },
            {
                "symbol": "AAPL20220720P120",
                "type": "option",
                "underlying": "AAPL",
                "strike": 120,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.8300000000000027,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.8400000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P120",
                "spreadPct": 0.0026075619295957706,
                "midBidAsk": 3.8350000000000026
            },
            {
                "symbol": "AAPL20220720C121",
                "type": "option",
                "underlying": "AAPL",
                "strike": 121,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.8500000000000028,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.8600000000000025,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C121",
                "spreadPct": 0.0025940337224383348,
                "midBidAsk": 3.8550000000000026
            },
            {
                "symbol": "AAPL20220720P121",
                "type": "option",
                "underlying": "AAPL",
                "strike": 121,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.8700000000000028,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.8800000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P121",
                "spreadPct": 0.0025806451612902658,
                "midBidAsk": 3.8750000000000027
            },
            {
                "symbol": "AAPL20220720C122",
                "type": "option",
                "underlying": "AAPL",
                "strike": 122,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.890000000000003,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.9000000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C122",
                "spreadPct": 0.002567394094993525,
                "midBidAsk": 3.8950000000000027
            },
            {
                "symbol": "AAPL20220720P122",
                "type": "option",
                "underlying": "AAPL",
                "strike": 122,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.910000000000003,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.9200000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P122",
                "spreadPct": 0.0025542784163473256,
                "midBidAsk": 3.9150000000000027
            },
            {
                "symbol": "AAPL20220720C123",
                "type": "option",
                "underlying": "AAPL",
                "strike": 123,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.930000000000003,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.9400000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C123",
                "spreadPct": 0.0025412960609910496,
                "midBidAsk": 3.9350000000000027
            },
            {
                "symbol": "AAPL20220720P123",
                "type": "option",
                "underlying": "AAPL",
                "strike": 123,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.950000000000003,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.9600000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P123",
                "spreadPct": 0.002528445006321057,
                "midBidAsk": 3.9550000000000027
            },
            {
                "symbol": "AAPL20220720C124",
                "type": "option",
                "underlying": "AAPL",
                "strike": 124,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.970000000000003,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 3.9800000000000026,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C124",
                "spreadPct": 0.0025157232704401964,
                "midBidAsk": 3.9750000000000028
            },
            {
                "symbol": "AAPL20220720P124",
                "type": "option",
                "underlying": "AAPL",
                "strike": 124,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 3.990000000000003,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.000000000000003,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P124",
                "spreadPct": 0.0025031289111388686,
                "midBidAsk": 3.9950000000000028
            },
            {
                "symbol": "AAPL20220720C125",
                "type": "option",
                "underlying": "AAPL",
                "strike": 125,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.0100000000000025,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.020000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C125",
                "spreadPct": 0.0024906600249065456,
                "midBidAsk": 4.015000000000002
            },
            {
                "symbol": "AAPL20220720P125",
                "type": "option",
                "underlying": "AAPL",
                "strike": 125,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.030000000000002,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.040000000000002,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P125",
                "spreadPct": 0.0024783147459726844,
                "midBidAsk": 4.035000000000002
            },
            {
                "symbol": "AAPL20220720C126",
                "type": "option",
                "underlying": "AAPL",
                "strike": 126,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.050000000000002,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.060000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C126",
                "spreadPct": 0.0024660912453760256,
                "midBidAsk": 4.0550000000000015
            },
            {
                "symbol": "AAPL20220720P126",
                "type": "option",
                "underlying": "AAPL",
                "strike": 126,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.070000000000001,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.080000000000001,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P126",
                "spreadPct": 0.002453987730061297,
                "midBidAsk": 4.075000000000001
            },
            {
                "symbol": "AAPL20220720C127",
                "type": "option",
                "underlying": "AAPL",
                "strike": 127,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.090000000000001,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.1000000000000005,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C127",
                "spreadPct": 0.0024420024420023895,
                "midBidAsk": 4.095000000000001
            },
            {
                "symbol": "AAPL20220720P127",
                "type": "option",
                "underlying": "AAPL",
                "strike": 127,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.11,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.12,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P127",
                "spreadPct": 0.0024301336573511025,
                "midBidAsk": 4.115
            },
            {
                "symbol": "AAPL20220720C128",
                "type": "option",
                "underlying": "AAPL",
                "strike": 128,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.13,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.14,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C128",
                "spreadPct": 0.0024183796856105896,
                "midBidAsk": 4.135
            },
            {
                "symbol": "AAPL20220720P128",
                "type": "option",
                "underlying": "AAPL",
                "strike": 128,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.1499999999999995,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.159999999999999,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P128",
                "spreadPct": 0.0024067388688326806,
                "midBidAsk": 4.154999999999999
            },
            {
                "symbol": "AAPL20220720C129",
                "type": "option",
                "underlying": "AAPL",
                "strike": 129,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.169999999999999,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.179999999999999,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C129",
                "spreadPct": 0.002395209580838273,
                "midBidAsk": 4.174999999999999
            },
            {
                "symbol": "AAPL20220720P129",
                "type": "option",
                "underlying": "AAPL",
                "strike": 129,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.189999999999999,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.199999999999998,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P129",
                "spreadPct": 0.0023837902264600215,
                "midBidAsk": 4.1949999999999985
            },
            {
                "symbol": "AAPL20220720C130",
                "type": "option",
                "underlying": "AAPL",
                "strike": 130,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.209999999999998,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.219999999999998,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C130",
                "spreadPct": 0.0023724792408065932,
                "midBidAsk": 4.214999999999998
            },
            {
                "symbol": "AAPL20220720P130",
                "type": "option",
                "underlying": "AAPL",
                "strike": 130,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.229999999999998,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.2399999999999975,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P130",
                "spreadPct": 0.0023612750885477667,
                "midBidAsk": 4.234999999999998
            },
            {
                "symbol": "AAPL20220720C131",
                "type": "option",
                "underlying": "AAPL",
                "strike": 131,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.249999999999997,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.259999999999997,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C131",
                "spreadPct": 0.002350176263219693,
                "midBidAsk": 4.254999999999997
            },
            {
                "symbol": "AAPL20220720P131",
                "type": "option",
                "underlying": "AAPL",
                "strike": 131,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.269999999999997,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.279999999999997,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P131",
                "spreadPct": 0.0023391812865496595,
                "midBidAsk": 4.274999999999997
            },
            {
                "symbol": "AAPL20220720C132",
                "type": "option",
                "underlying": "AAPL",
                "strike": 132,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.2899999999999965,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.299999999999996,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C132",
                "spreadPct": 0.0023282887077997194,
                "midBidAsk": 4.294999999999996
            },
            {
                "symbol": "AAPL20220720P132",
                "type": "option",
                "underlying": "AAPL",
                "strike": 132,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.309999999999996,
                "bid_date": 1657753555791,
                "ask_date": 1657753555791,
                "ask": 4.319999999999996,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.791-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P132",
                "spreadPct": 0.0023174971031285738,
                "midBidAsk": 4.314999999999996
            },
            {
                "symbol": "AAPL20220720C133",
                "type": "option",
                "underlying": "AAPL",
                "strike": 133,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.329999999999996,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.339999999999995,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C133",
                "spreadPct": 0.002306805074971118,
                "midBidAsk": 4.3349999999999955
            },
            {
                "symbol": "AAPL20220720P133",
                "type": "option",
                "underlying": "AAPL",
                "strike": 133,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.349999999999995,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.359999999999995,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P133",
                "spreadPct": 0.0022962112514350857,
                "midBidAsk": 4.354999999999995
            },
            {
                "symbol": "AAPL20220720C134",
                "type": "option",
                "underlying": "AAPL",
                "strike": 134,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.369999999999995,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.379999999999995,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C134",
                "spreadPct": 0.00228571428571424,
                "midBidAsk": 4.374999999999995
            },
            {
                "symbol": "AAPL20220720P134",
                "type": "option",
                "underlying": "AAPL",
                "strike": 134,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.389999999999994,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.399999999999994,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P134",
                "spreadPct": 0.002275312855517588,
                "midBidAsk": 4.394999999999994
            },
            {
                "symbol": "AAPL20220720C135",
                "type": "option",
                "underlying": "AAPL",
                "strike": 135,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.409999999999994,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.419999999999994,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C135",
                "spreadPct": 0.0022650056625141113,
                "midBidAsk": 4.414999999999994
            },
            {
                "symbol": "AAPL20220720P135",
                "type": "option",
                "underlying": "AAPL",
                "strike": 135,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.4299999999999935,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.439999999999993,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P135",
                "spreadPct": 0.0022547914317925144,
                "midBidAsk": 4.434999999999993
            },
            {
                "symbol": "AAPL20220720C136",
                "type": "option",
                "underlying": "AAPL",
                "strike": 136,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.449999999999993,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.459999999999993,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C136",
                "spreadPct": 0.0022446689113355335,
                "midBidAsk": 4.454999999999993
            },
            {
                "symbol": "AAPL20220720P136",
                "type": "option",
                "underlying": "AAPL",
                "strike": 136,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.469999999999993,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.479999999999992,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P136",
                "spreadPct": 0.002234636871508336,
                "midBidAsk": 4.4749999999999925
            },
            {
                "symbol": "AAPL20220720C137",
                "type": "option",
                "underlying": "AAPL",
                "strike": 137,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.489999999999992,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.499999999999992,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C137",
                "spreadPct": 0.0022246941045605795,
                "midBidAsk": 4.494999999999992
            },
            {
                "symbol": "AAPL20220720P137",
                "type": "option",
                "underlying": "AAPL",
                "strike": 137,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.509999999999992,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.519999999999992,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P137",
                "spreadPct": 0.0022148394241417067,
                "midBidAsk": 4.514999999999992
            },
            {
                "symbol": "AAPL20220720C138",
                "type": "option",
                "underlying": "AAPL",
                "strike": 138,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.529999999999991,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.539999999999991,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C138",
                "spreadPct": 0.002205071664829064,
                "midBidAsk": 4.534999999999991
            },
            {
                "symbol": "AAPL20220720P138",
                "type": "option",
                "underlying": "AAPL",
                "strike": 138,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.549999999999991,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.559999999999991,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P138",
                "spreadPct": 0.0021953896816684537,
                "midBidAsk": 4.554999999999991
            },
            {
                "symbol": "AAPL20220720C139",
                "type": "option",
                "underlying": "AAPL",
                "strike": 139,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.5699999999999905,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.57999999999999,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C139",
                "spreadPct": 0.002185792349726734,
                "midBidAsk": 4.57499999999999
            },
            {
                "symbol": "AAPL20220720P139",
                "type": "option",
                "underlying": "AAPL",
                "strike": 139,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.58999999999999,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.59999999999999,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P139",
                "spreadPct": 0.0021762785636561064,
                "midBidAsk": 4.59499999999999
            },
            {
                "symbol": "AAPL20220720C140",
                "type": "option",
                "underlying": "AAPL",
                "strike": 140,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.60999999999999,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.6199999999999894,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C140",
                "spreadPct": 0.002166847237269731,
                "midBidAsk": 4.6149999999999896
            },
            {
                "symbol": "AAPL20220720P140",
                "type": "option",
                "underlying": "AAPL",
                "strike": 140,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.629999999999989,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.639999999999989,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P140",
                "spreadPct": 0.00215749730312833,
                "midBidAsk": 4.634999999999989
            },
            {
                "symbol": "AAPL20220720C141",
                "type": "option",
                "underlying": "AAPL",
                "strike": 141,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.649999999999989,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.659999999999989,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C141",
                "spreadPct": 0.002148227712137446,
                "midBidAsk": 4.654999999999989
            },
            {
                "symbol": "AAPL20220720P141",
                "type": "option",
                "underlying": "AAPL",
                "strike": 141,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.669999999999988,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.679999999999988,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P141",
                "spreadPct": 0.00213903743315504,
                "midBidAsk": 4.674999999999988
            },
            {
                "symbol": "AAPL20220720C142",
                "type": "option",
                "underlying": "AAPL",
                "strike": 142,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.689999999999988,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.699999999999988,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C142",
                "spreadPct": 0.002129925452609119,
                "midBidAsk": 4.694999999999988
            },
            {
                "symbol": "AAPL20220720P142",
                "type": "option",
                "underlying": "AAPL",
                "strike": 142,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.7099999999999875,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.719999999999987,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P142",
                "spreadPct": 0.002120890774125093,
                "midBidAsk": 4.714999999999987
            },
            {
                "symbol": "AAPL20220720C143",
                "type": "option",
                "underlying": "AAPL",
                "strike": 143,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.729999999999987,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.739999999999987,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C143",
                "spreadPct": 0.0021119324181625796,
                "midBidAsk": 4.734999999999987
            },
            {
                "symbol": "AAPL20220720P143",
                "type": "option",
                "underlying": "AAPL",
                "strike": 143,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.749999999999987,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.7599999999999865,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P143",
                "spreadPct": 0.00210304942166137,
                "midBidAsk": 4.754999999999987
            },
            {
                "symbol": "AAPL20220720C144",
                "type": "option",
                "underlying": "AAPL",
                "strike": 144,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.769999999999986,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.779999999999986,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C144",
                "spreadPct": 0.0020942408376962967,
                "midBidAsk": 4.774999999999986
            },
            {
                "symbol": "AAPL20220720P144",
                "type": "option",
                "underlying": "AAPL",
                "strike": 144,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.789999999999986,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.799999999999986,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P144",
                "spreadPct": 0.0020855057351407335,
                "midBidAsk": 4.794999999999986
            },
            {
                "symbol": "AAPL20220720C145",
                "type": "option",
                "underlying": "AAPL",
                "strike": 145,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.809999999999985,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.819999999999985,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C145",
                "spreadPct": 0.0020768431983384877,
                "midBidAsk": 4.814999999999985
            },
            {
                "symbol": "AAPL20220720P145",
                "type": "option",
                "underlying": "AAPL",
                "strike": 145,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.829999999999985,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.839999999999985,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P145",
                "spreadPct": 0.00206825232678383,
                "midBidAsk": 4.834999999999985
            },
            {
                "symbol": "AAPL20220720C146",
                "type": "option",
                "underlying": "AAPL",
                "strike": 146,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.8499999999999845,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.859999999999984,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C146",
                "spreadPct": 0.0020597322348094374,
                "midBidAsk": 4.854999999999984
            },
            {
                "symbol": "AAPL20220720P146",
                "type": "option",
                "underlying": "AAPL",
                "strike": 146,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.869999999999984,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.879999999999984,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P146",
                "spreadPct": 0.0020512820512820144,
                "midBidAsk": 4.874999999999984
            },
            {
                "symbol": "AAPL20220720C147",
                "type": "option",
                "underlying": "AAPL",
                "strike": 147,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.889999999999984,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.8999999999999835,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C147",
                "spreadPct": 0.002042900919305377,
                "midBidAsk": 4.894999999999984
            },
            {
                "symbol": "AAPL20220720P147",
                "type": "option",
                "underlying": "AAPL",
                "strike": 147,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.909999999999983,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.919999999999983,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P147",
                "spreadPct": 0.0020345879959307875,
                "midBidAsk": 4.914999999999983
            },
            {
                "symbol": "AAPL20220720C148",
                "type": "option",
                "underlying": "AAPL",
                "strike": 148,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.929999999999983,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.939999999999983,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C148",
                "spreadPct": 0.0020263424518743305,
                "midBidAsk": 4.934999999999983
            },
            {
                "symbol": "AAPL20220720P148",
                "type": "option",
                "underlying": "AAPL",
                "strike": 148,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.949999999999982,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.959999999999982,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P148",
                "spreadPct": 0.0020181634712411346,
                "midBidAsk": 4.954999999999982
            },
            {
                "symbol": "AAPL20220720C149",
                "type": "option",
                "underlying": "AAPL",
                "strike": 149,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "call",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.969999999999982,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.979999999999982,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720C149",
                "spreadPct": 0.002010050251256246,
                "midBidAsk": 4.974999999999982
            },
            {
                "symbol": "AAPL20220720P149",
                "type": "option",
                "underlying": "AAPL",
                "strike": 149,
                "last": null,
                "expiration_date": "2022-07-20",
                "expiration_type": "weeklies",
                "option_type": "put",
                "root_symbol": null,
                "greeks": null,
                "trade_date": null,
                "volume": null,
                "open_interest": null,
                "bid": 4.989999999999982,
                "bid_date": 1657753555792,
                "ask_date": 1657753555792,
                "ask": 4.999999999999981,
                "spreadCents": 0.009999999999999787,
                "bidDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "askDate": "2022-07-13T19:05:55.792-04:00[America/New_York]",
                "optionSymbol": "AAPL20220720P149",
                "spreadPct": 0.002002002002001967,
                "midBidAsk": 4.9949999999999815
            }
        ]
    }
]
 */

//outgoing trade plan with option choices
// {
//   "quantityBasis": "DOLLARS",
//   "status": "ARMED",
//   "dataSource": "LIVE",
//   "brokerage": "BACKTEST",
//   "accountNumber": "BACKTEST",
//   "goLong": true,
//   "goShort": false,
//   "workOrders": false,
//   "workOrdersOnEntry": true,
//   "workOrdersOnExit": true,
//   "strategy": "BASIC_TRADING_SYSTEM",
//   "instrument": "option",
//   "symbol": "aapl",
//   "startPeriod": "open",
//   "setup": "BREAK",
//   "terminateWhenFlat": true,
//   "restartSystemOncePositionHasFlattened": false,
//   "flattenAtMarketClose": false,
//   "startHour": 9,
//   "startMin": 30,
//   "evaluateQuotes": false,
//   "persistTradePlanOnFailedFiltersOrValidation": false,
//   "useDifferentWorkOrderFieldsOnExit": false,
//   "getAwayOffsetPctOnExit": 0.1,
//   "pctBetweenBidAskOnExit": 0.5,
//   "pctIncreasePerOrderAttemptOnExit": 0.1,
//   "useDifferentWorkOrderFieldsOnTarget": false,
//   "getAwayOffsetPctOnTarget": 0.1,
//   "pctBetweenBidAskOnTarget": 0.5,
//   "pctIncreasePerOrderAttemptOnTarget": 0.1,
//   "entryTactics": {
//       "MARKET_ORDER": {
//           "quantityBasisValue": "1000"
//       }
//   },
//   "exitTactics": {
//       "LOSS_LIMIT": {
//           "amount": "100",
//           "lossLimitMode": "AMOUNT"
//       }
//   },
//   "setupDetails": {
//       "BREAK": {
//           "priceToBreak": 1000
//       }
//   },
//   "optionManagement": {
//       "optionChoices": {
//           "choices": {
//               "2022-03-25": [
//                   "AAPL220325C00117000",
//                   "AAPL220325P00119000"
//               ],
//               "2022-03-18": [
//                   "AAPL220318C00070000",
//                   "AAPL220318C00105000",
//                   "AAPL220318C00185000",
//                   "AAPL220318P00085000",
//                   "AAPL220318P00182500"
//               ]
//           },
//           "dataSource": "LIVE"
//       },
//       "expirationDates": [
//           "Fri Mar 18 00:00:00 EDT 2022",
//           "Fri Mar 25 00:00:00 EDT 2022"
//       ],
//       "buyOrSellOption": "LONG",
//       "OTM": false,
//       "ITM": false,
//       "optionChainStrategy": {
//           "hugCurrentPrice": {
//               "numOfStrikes": 5
//           }
//       },
//       "optionFilters": {
//           "tightSpreadPct": {
//               "maxOptionSpreadPct": 0.05
//           }
//       }
//   },
//   "restartSettings": {
//       "maxRestartAttempts": 0,
//       "isMaxRestartAttempts": false,
//       "maxLoss": 0,
//       "isMaxLoss": false,
//       "maxMinutesInTradePlan": 0,
//       "isMaxMinutesInTradePlan": false,
//       "numberOfTrades": 0,
//       "isNumberOfTrades": false,
//       "deactivateOnRestart": false
//   },
//   "savedConfigurationTitle": "options",
//   "id": 1647362767327
// }

/*
{
    "choices": {
        "2022-07-18": [
            "AAPL20220718C50"
        ],
        "2022-07-25": []
    },
    "dataSource": "LIVE"
}
*/
